/*
vue-i18n (v8.x) guide
https://kazupon.github.io/vue-i18n/guide/formatting.html
*/

import { formattedDate } from './dateTimeFormatter.ts';

const en = {
    booking_status_labels: {
        cancelled: 'Canceled',
        confirmed: 'Confirmed',
        failed: 'Failed',
        pending: 'Pending',
        waiting_for_payment: 'Waiting for payment',
    },
    ticket_purchase_status_labels: {
        in_process: 'In process',
        payment_failed: 'Payment failed',
        payment_processing: 'Payment processing',
        unspecified: 'Unspecified',
    },
    countWithUnit: {
        days: '{count} Day | {count} Days',
        followers: 'no Follower | {count} Follower | {count} Followers',
        hours: '{count} Hour | {count} Hours',
        months: '{count} Month | {count} Months',
        weeks: '{count} Week | {count} Weeks',
        years: '{count} Year | {count} Years',
    },
    coWeekdaySelector: {
        ariaLabel: 'Select weekdays',
        weekdays: {
            fridayShort: 'Fr',
            mondayShort: 'Mo',
            saturdayShort: 'Sa',
            sundayShort: 'Su',
            thursdayShort: 'Th',
            tuesdayShort: 'Tu',
            wednesdayShort: 'We',
        },
    },
    coUserInvite: {
        csvClear: 'Clear CSV',
        csvErrorEmpty: 'The CSV file is empty.',
        csvErrorInvalidType: 'Please upload a valid CSV file.',
        csvErrorNoEmails: 'No valid email addresses were found in the CSV file.',
        csvErrorRead: 'Unable to read the CSV file.',
        csvPreviewEmailColumn: '@:labels.email',
        csvPreviewNumberColumn: '#',
        csvPreviewTitle: 'Preview ({count} invite) | Preview ({count} invites)',
        csvSelectButton: 'Choose file',
        csvSelectedFile: 'Selected file: {name}',
        csvTemplateDownload: 'Example CSV',
        csvTemplateHelp: 'Need a template? Download the example CSV and replace the email addresses with your own: {0}',
        csvUploadLabel: 'Invite a list of emails via CSV',
        inviteGeneralError: 'Failed to send invite link',
        invitePartialError: 'Some invites could not be sent',
        inviteperemail: 'Invite via email',
        inviteResultFailure: 'Failed to send invite',
        inviteResultStatus: 'Status',
        inviteResultsTitle: 'Invite results',
        inviteResultSuccess: 'Invite sent',
        inviteSuccessMessage: 'Invite link sent',
        manualEntryHelp: 'Enter a valid email and press enter.',
        modalTitle: '@:labels.invitemember',
        progressInfo: 'Invites are sent one by one. Please keep this window open until the process is finished.',
        progressStatus: '{current} of {total} invites sent',
        seeInviteList: 'See invite list',
        sendButtonLabel: 'Send invite | Send invites',
    },
    communityFeed: {
        postNudge: {
            dismissed: 'Post suggestions will stay hidden for 14 days.',
            nextSuggestion: 'Next suggestion',
            suggestions: {
                ama: {
                    label: 'Host an AMA',
                    title: 'Invite your community to a Q&A',
                    message: 'Host an event where members can ask questions and join a focused conversation.',
                },
                challenge: {
                    label: 'Start a challenge',
                    title: 'Give people a reason to return',
                    message: 'Create a simple activity members can join again and again to build a shared habit.',
                },
                guide: {
                    label: 'Share a guide or template',
                    title: 'Share something useful',
                    message: 'Publish a practical resource members will save, share, and revisit.',
                },
                meme: {
                    label: 'Share a meme',
                    title: 'Share an inside joke',
                    message: 'A relevant meme or inside joke can spark reactions and strengthen belonging.',
                },
                poll: {
                    label: 'Create a poll',
                    title: 'Make participation easy',
                    message: 'Ask a quick question or offer a simple choice to encourage more people to join in.',
                },
                question: {
                    label: 'Ask a question',
                    title: 'Start a conversation',
                    message: 'Ask a focused question that invites thoughtful replies and useful insights.',
                },
                spotlight: {
                    label: 'Celebrate a member',
                    title: 'Put a member in the spotlight',
                    message: 'Highlight a milestone or helpful contribution to strengthen your community.',
                },
            },
        },
    },
    datetime: ({ named }) =>
        formattedDate(named('date'), named('format'), named('locale') ? named('locale') : null, named('customFormat')),
    errors: {
        tooManyRequests: 'Too many requests from your IP address. Please wait a moment and try again.',
        accessManagement: {
            // ErrAccessManagementSystemIsNotAvailable is used when the access management system is not available/offline.
            ErrAccessManagementSystemIsNotAvailable:
                'Access management system is not available/offline. Make sure access management system is online and available and try again.',
            // ErrAccessManagementSystemIsNotConfigured is used when the access management system is not configured.
            ErrAccessManagementSystemIsNotConfigured:
                'Access management system is not configured, contact the support team.',
            // ErrAccessManagementSystemUnexpectedError is used when an unexpected error occurs in the access management system.
            ErrAccessManagementSystemUnexpectedError:
                'Unexpected error occurred in access management system. Make sure access management system is online and available and try again.',
            // ErrEmailIsRequired is used when email is required but not provided.
            ErrEmailIsRequired: 'Invalid request: Email is required, report this to the support team.',
            // ErrFailedToAssignKeyToUser is used when assigning a key to a user fails and should try another key.
            ErrFailedToAssignKeyToUser:
                'Failed to assign key to user, this likely means the key is invalid or already in use. Please try another key.',
            ErrFailedToAssignPolicyToUser:
                'Failed to assign policy to user, this likely means the policy is misconfigured or out of sync with coapp. Please resync the policies in coapp and try again.',
            // ErrFailedToCreateUser is used when user creation in access management system fails.
            ErrFailedToCreateUser:
                'Failed to create user in access management system. Make sure access management system is online and available and try again.',
            // ErrFailedToCreateUserInDB is used when user creation in the database fails.
            ErrFailedToCreateUserInDB: 'Failed to create user in database, contact the support team.',
            // ErrFailedToDeleteUser is used when user deletion in access management system fails.
            ErrFailedToDeleteUser:
                'Failed to delete user in access management system. Make sure access management system is online and available and try again.',
            // ErrFailedToDeleteUserInDB is used when user deletion in the database fails.
            ErrFailedToDeleteUserInDB: 'Failed to delete user in database, contact the support team.',
            // ErrFailedToFetchUser is used when fetching user from access management system fails.
            ErrFailedToFetchUser:
                'Failed to fetch user from access management system. Make sure access management system is online and available and try again.',
            // ErrFailedToFetchUserFromDB is used when fetching user from the database fails
            ErrFailedToFetchUserFromDB: 'Failed to fetch user from database, contact the support team.',
            // ErrFailedToUpdateUser is used when user update in access management system fails.
            ErrFailedToUpdateUser:
                'Failed to  user in access management system. Make sure access management system is online and available and try again.',
            // ErrFailedToUpdateUserInDB is used when user update in the database fails.
            ErrFailedToUpdateUserInDB: 'Failed to update user in database, contact the support team.',
            // ErrIdentityIDIsRequired returned if client tries to update, delete or fetch user without providing access identity ID.
            ErrIdentityIDIsRequired: 'Invalid request: Identity ID is required, report this to the support team.',
            // ErrSpaceIDIsRequired is used when space ID is required but not provided.
            ErrSpaceIDIsRequired: 'Invalid request: Space ID is required, report this to the support team.',
            ErrUnspecified: 'An unspecified error occurred in the access management system, contact the support team.',
            // ErrUserIsNotFound is used when user is not found in access management system.
            ErrUserIsNotFound: 'User not found in access management system',
            // ErrUserIsNotFoundInDB is used when user is not found in the database.
            ErrUserIsNotFoundInDB: 'User not found in database',
        },
        atLeastOneMethod: 'At least one payment method must be enabled.',
        failedGeneric: 'An error occurred. Please try again.',
        failedToSaveChanges: 'Failed to save changes',
        fileTooLarge: 'File is too large. Maximum file size is {size} MB.',
        inputinvalid: 'Invalid input',
        inputrequired: 'This field is required',
        invalid: '@:labels.invalid',
        invalidLoginCredentials: 'Invalid login credentials',
        localstorageDisabled:
            'LocalStorage is disabled. Without this browser feature, coapp cannot be used. Please enable LocalStorage in your browser settings to continue.',
        mustBeValidEmail: 'Input must be a valid email address',
        noPaymentMethods: "You don't have any payment method. Please create one before subscribing to plan",
        uploadFailed: 'Upload failed, please try again.',
        usernotfound: 'User not found',
    },
    // Event participation modes shown during event creation.
    eventParticipationOptions: {
        external: {
            description:
                'Let members register here and also show a link to an external registration page. No price will be displayed.',
            title: 'External registration link',
        },
        internal: {
            description:
                'Use tickets to manage attendance, create different participation categories, and generate revenue. Paid tickets can only be set up by admins. If you create your event with tickets, you cannot switch to another mode later. You can always edit or add tickets.',
            nonAdminNotice: 'You can create free tickets. Contact an admin if this event should offer paid tickets.',
            title: 'Offer tickets',
        },
        rsvp: {
            description:
                'Let members join without tickets. Guests can view public events, but need a ticket to register.',
            guestRegistrationInfo:
                'Non-members are currently unable to register for this event because it has no tickets.',
            guestRegistrationNotice: {
                title: 'Who can register?',
                options: {
                    rsvp: {
                        title: 'With "RSVP"',
                        description: 'Only registered community members can register.',
                    },
                    tickets: {
                        title: 'With "Offer tickets"',
                        description:
                            'Guests outside your community can also register. To do this, enable "Offer tickets" and create at least one ticket. It can also be free.',
                    },
                },
            },
            title: 'RSVP',
        },
    },
    // Event format modes shown during event creation.
    eventFormatOptions: {
        hybrid: {
            description: 'Share both a physical location and a link for attendees.',
            title: 'Hybrid event',
        },
        online: {
            description:
                'Host the event online and show a video conference or livestream link. The link is only visible to attendees.',
            title: 'Online event',
        },
        physical: {
            description: 'The event takes place at a physical location. Show the venue or meeting point.',
            title: 'Physical event',
        },
    },
    labels: {
        // Headings and controls for the legal and compliance settings.
        legalandcompliance: 'Legal and Compliance',
        purchaseCancellationPolicy: 'Purchase Cancellation Policy',
        editPurchaseCancellationPolicy: 'Edit Purchase Cancellation Policy',
        enablePurchaseCancellationPolicy: 'Enable purchase cancellation policy',
        purchasewithdrawalform: 'Purchase Withdrawal Form',
        editAccessibilityPolicy: 'Edit Accessibility Policy',
        editImprintAndPrivacyPolicy: 'Edit Imprint and Privacy Policy',
        editTermsAndConditions: 'Edit Terms and Conditions',
        enableAccessibilityPolicy: 'Enable accessibility policy',
        enableImprintAndPrivacyPolicy: 'Enable imprint and privacy policy',
        enablePurchaseWithdrawalForm: 'Enable purchase withdrawal form',
        enableTermsAndConditions: 'Enable terms and conditions',
        about: 'About',
        aboutme: 'About me',
        applePodcastsAudioPlayer: 'Apple Podcasts audio player',
        audioSection: 'Podcast / Audio Section',
        audioUrl: 'Podcast or audio URL',
        accept: 'Accept',
        acceptprivacy: 'I have read and understood the {0}',
        acceptprivacyandterms: 'I have read and understood the {0} and accept the {1}',
        acceptterms: 'I have read and accept the {0}',
        access: 'Access',
        accessibilitypolicy: 'Accessibility Policy',
        accesskey: 'Access key',
        accessmanagement: 'Physical Access Control',
        accesspolicy: 'Access Policy',
        account: 'Account',
        accountcreated: 'Account created',
        accountsettings: 'Account settings',
        actions: 'Actions',
        activatedeactivate: 'Activate / deactivate',
        activatepayments: 'Activate payments',
        active: 'Active',
        activemembers: 'Active members',
        activity: 'Activity',
        accountingcode: 'Accounting code',
        add: 'Add',
        addaddress: 'Add a new address',
        addblockingrule: 'Add blocking rule',
        addbookingresourcegroup: 'Add booking resource group',
        addcalendaraccount: 'Add calendar account',
        addemail: 'add email',
        addavailability: 'Add availability',
        addkey: 'Add key',
        addoverride: 'Add override',
        addplanupgrade: 'Add plan upgrade',
        addresource: 'Add resource',
        address: 'Address',
        addressadditionalinformation: 'Additional information',
        addrow: 'Add row',
        addrule: 'add rule',
        addsection: 'Add Section',
        addtogoogle: 'Add to Google',
        addtooutlook: 'Add to Outlook',
        addupgrade: 'Add upgrade',
        addvideoconferencelink: 'Video conference link',
        unlock: 'Unlock',
        admin: 'Admin',
        adminadd: 'Add administrator',
        administrators: 'Administrators',
        // Link to administrator management from legal settings.
        viewAdministrators: 'View administrators',
        adminremove: 'Remove administrator',
        afterbookingbehavior: 'After booking behavior',
        afterbookingbuttontitle: 'After booking button title (Optional)',
        afterbookingbuttonurl: 'After booking button url (Optional)',
        afterbookinginfoheading: 'Information for user to show after booking confirmation',
        afterbookingtext: 'After booking text (Optional)',
        afterbookingtitle: 'After booking title (Optional)',
        agreements: 'Agreements',
        all: 'All',
        allowedvisibilities: 'Allowed visibilities',
        allresources: 'All resources',
        amount: 'Amount',
        and: 'and',
        announcement: 'Announcement',
        announcementmake: 'Make @.lower:labels.announcement',
        announcementsentby: '@:labels.announcement sent by {name}',
        apikey: 'API Key',
        apikeycreated: 'API key created',
        apply: 'Apply',
        applynewcolors: 'Apply new colors',
        archive: 'Archive',
        archived: 'Archived',
        aspectratio: 'Aspect ratio',
        aspectratiolandscape: 'Landscape',
        aspectratioportrait: 'Portrait',
        aspectratiosquare: 'Square',
        assignaplan: 'Assign a plan',
        assignkey: 'Assign a key',
        assignsubscription: 'Assign subscription',
        attend: 'Attend',
        attendees: 'Attendees',
        authentication: 'Authentication',
        authenticationtype: 'Authentication Type',
        author: 'Author',
        authtoken: 'Auth Token',
        authurl: 'Auth URL',
        availability: 'Availability',
        availablebookingupgrades: 'Available booking upgrades',
        availableforbooking: 'Available for booking',
        awaynotice: 'Away notice',
        back: 'Back',
        backtologin: 'Back to login',
        backfill: 'Backfill',
        bankcode: 'Bank code',
        baseurl: 'Base URL',
        bestoptions: 'Best options',
        billing: 'Billing',
        billingadditionalinfo: 'Billing: Additional info',
        billingaddress: 'Billing address',
        billingaddressedit: '@:labels.edit @.lower:labels.billingaddress',
        billingadmin: '@:labels.owner',
        billingcity: 'Billing: City',
        billingcompany: 'Billing: Company',
        billingcountry: 'Billing: Country',
        billingcycle: 'Billing cycle',
        billingcycleend: 'Billing cycle end',
        billingcyclenotavailable: 'No Billing cycle',
        billingcyclestart: 'Billing cycle start',
        billingAccess: 'Billing access',
        billingmanager: 'Billing manager',
        billingphone: 'Billing: Phone',
        billingpostalcode: 'Billing: Postal code',
        billingstreet: 'Billing: Street',
        billingvatid: 'Billing: VAT ID',
        birthdate: 'Date of birth',
        block: 'Block',
        blockingrules: 'Blocking rules',
        book: 'Book',
        bookedasadmin: 'Booked as admin',
        booking: 'Booking',
        'booking-checkout': {
            bookingheader: 'Book {resource}',
            calendar: 'Add this booking to your calendar',
            cancellationpolicy:
                'This booking can be cancelled until its start time free of charge. After that, this booking is non-refundable. | This booking can be cancelled until {count} hours before its start time free of charge. After that, this booking is non-refundable.',
            couponnotneded: 'The booking is already free of charge for you, so the coupon will not be applied.',
            error: 'Unfortunately something went wrong while creating your booking',
            isexhausted: 'The selected time is not available for booking.',
            lockavailabilitynotice:
                "This resource is secured by a lock. You can unlock it yourself in the details view of this booking under 'My bookings' from {available_from} until {available_until}.",
            processing: 'Your booking is processed.',
            seebookings: 'See my bookings',
            success: 'Your booking was successful',
            toolong:
                'This resource allows a maximum booking duration of {0} minutes. The end time has been adjusted accordingly.',
            tooshort:
                'This resource requires a minimum booking duration of {0} minutes. The end time has been adjusted accordingly.',
        },
        bookingBy: 'Created by',
        bookingcalendar: 'Booking calendar',
        bookingcancel: '@:labels.cancel @.lower:labels.booking',
        bookingcategories: 'Booking categories',
        bookingconditions: '@:labels.booking conditions',
        bookingconfirm: 'Book now',
        bookingconfirmationemailcontent: 'Booking confirmation email content',
        bookingconfirmpaid: 'Complete paid booking',
        bookingfor: 'Booking for',
        bookingFor: 'Booking for',
        bookingInterval: 'Booking interval',
        bookinggranularity: 'Booking granularity',
        bookinggranularity15minutes: '15 minutes',
        bookinggranularity30minutes: '30 minutes',
        bookinggranularityday: 'Day',
        bookinggranularityhour: 'Hour',
        bookingOverridePrice: 'Override price',
        bookingresource: 'Booking Resource',
        bookingresourcecreate: 'Create Booking Resource',
        bookingresourceedit: 'Edit Booking Resource',
        bookingresources: 'Booking Resources',
        allowstartedtimeslotbooking: 'Allow booking after start time',
        bookings: 'Bookings',
        bookingshistory: 'Booking history',
        bookingslot: 'Booking slot',
        bookingsmy: 'My Bookings',
        bookingsother: 'Other bookings',
        bookingstart: 'Booking start',
        bookingsupcoming: 'Upcoming bookings',
        bookingTitle: 'Title of booking',
        bookingupgrade: 'Booking upgrade',
        bookwholeslot: 'Book whole slot',
        builtindomain: 'Built-in domain',
        bulkcreate: 'Bulk Create',
        bulkmembercreate: 'Bulk member create',
        buy: 'Buy',
        buyhere: 'Buy here',
        buynow: 'Buy now',
        buyPass: {
            expireDate: 'Expire date',
            maxHoursPerUse: 'Max. hours per use',
            messageOptional: 'Message (optional)',
            numberOfTimesToUse: 'Number of times to use',
            overridePrice: 'Override price',
            paymentDueToday: 'Payment due today',
            payWith: 'Pay with',
            priceIncludesTax: 'Price includes {percentage}% {name} VAT',
            setNewPriceOptional: 'Set a new price (optional)',
        },
        by: 'By',
        byme: '@:labels.mine',
        calendar: 'Calendar',
        calendarsynchronization: 'Calendar synchronization',
        cancel: 'Cancel',
        canceled: 'Canceled',
        cancellationnotice: 'Cancellation notice',
        cancellationnoticemessage: 'Cancellation possible up to {days} days before the event.',
        cancellationperiod: 'Cancellation Period',
        cancellationpolicy: 'Cancellation policy',
        cancellationpolicyindays: 'Cancellation policy (in days)',
        cancelling: 'Cancelling ...',
        cancelmembership: 'Cancel membership',
        cancelupcomingplan: 'Cancel upcoming plan',
        cancelupcomingsubscription: 'Cancel upcoming subscription',
        capacity: 'Capacity',
        cardpayments: 'Card payments',
        category: 'Category',
        cf: {
            addCustomField: 'Add Custom Field',
            dataType: 'Field Type',
            datatypeboolean: 'Yes/No (boolean)',
            datatypedate: 'Date',
            datatypelink: 'Link',
            datatypemultiselect: 'Multi Select',
            datatypenumber: 'Number',
            datatypeselect: 'Select',
            datatypetext: 'Text',
            editability: 'Who can edit this field',
            editabilityadmin: 'Admin',
            editabilityowner: 'Member (self)',
            editCustomField: 'Edit Custom Field',
            identifier: 'Internal field identifier (not visible)',
            isusedformatchingtooltip: 'These details are used to match profiles with each other.',
            label: 'Name',
            optionsValues: 'Options Values',
            optionsValuesHint: 'Comma separated values for select/multiselect',
            requestedOnSignUp: 'Requested on Sign Up',
            requestedonsignupnone: 'None',
            requestedonsignupoptional: 'Optional',
            requestedonsignuprequired: 'Required',
            sortOrder: 'Position',
            useformatching: 'Use for matching',
            useformatchingtooltip: 'Use the data in this field to match your members with each other.',
            visibility: 'Who can see this field',
            visibilityadmin: 'Admin',
            visibilitycommunity: 'Community',
            visibilityLevelsFromAdminPOV: {
                admin: 'Admins only',
                community: 'All members',
                owner: 'Admins and user only',
            },
            visibilityLevelsFromUserPOV: {
                admin: '',
                community: 'All members',
                owner: 'Admins only',
            },
            visibilityowner: 'Member (self)',
            visibilitypublic: 'Public',
        },
        change: 'Change',
        changehistory: 'Change history',
        changes: 'Changes',
        changessaved: '@:messages.changessaved',
        changestatus: 'Change status',
        channel: 'Channel',
        channeladd: '@:labels.add @.lower:labels.channel',
        channelEditTitle: 'Edit channel',
        channelname: '@:labels.channel @.lower:labels.name',
        channelnew: '@:labels.new @.lower:labels.channel',
        channelPermissions: {
            addPages: 'Who can add pages to this channel',
            admins: 'Admins',
            adminsOnly: 'Admins only',
            placeholder: 'Channel name',
        },
        channels: 'Channels',
        channelsandpages: 'Channels & Pages',
        channelspages: 'Channels & Pages',
        chargeautomatically: 'Charge automatically',
        chat: 'Chat',
        chatnew: '@:labels.new @.lower:labels.chat',
        chatuserhasleft: 'has left the chat',
        checkins: 'Check-ins',
        checkintime: 'Check-in time',
        checkout: 'Checkout',
        checkoutday: 'Checkout day',
        checkoutsessionid: 'Checkout session ID',
        checkouttime: 'Checkout time',
        checkouturl: 'Checkout URL',
        choose: 'Choose',
        choosefile: 'Choose file',
        chooseplan: 'Choose plan',
        city: 'City',
        clear: 'Clear',
        clearall: 'Clear all',
        clearallfilters: 'Clear all filters',
        clientid: 'Client ID',
        confirmWithdrawal: 'Confirm withdrawal',
        clientsecret: 'Client Secret',
        close: 'Close',
        closed: 'Closed',
        closedforbookings: 'Closed for bookings',
        coapp: 'coapp',
        coappaccount: 'coapp Account',
        coappmy: 'My coapp',
        coappsupport: 'coapp Support',
        coBookingCalendar: {
            bookingCalendar: 'Booking Calendar',
            busy: 'Unavailable',
        },
        codeSection: 'Code Section',
        collectionmethod: 'Collection method',
        color: 'Color',
        colorbackground: 'Background color',
        colorforeground: 'Foreground color',
        comment: 'Comment',
        commentdelete: '@:labels.delete @.lower:labels.comment',
        comments: 'Comments',
        commentsViewAll: 'View all comments',
        community: 'Community',
        communityrecentlyseen: 'Recently seen online',
        company: 'Company',
        complete: 'Complete',
        completeSetup: 'Complete the setup',
        configuration: 'Configuration',
        confirm: 'Confirm',
        confirmationcode: 'Confirmation code',
        connect: 'Connect',
        connected: 'Connected',
        connectnukiaccount: 'Connect your NUKI Web Account',
        connecttostripe: 'Connect to Stripe',
        contact: 'Contact',
        contactEmail: 'Contact email',
        contactcoappsupport: 'please contact the coapp support team',
        contactsaleslink: 'Contact our sales team',
        contactstripesupport: 'Contact Stripe support',
        contactsupport: 'Contact support',
        continue: 'Continue',
        contractWithdrawal: 'Withdraw from contract',
        contributers: 'Contributors',
        contributersadd: 'Add contributors',
        contributor: 'Contributor',
        cookies: '🍪 Cookies',
        copy: 'Copy',
        copyshareablelink: 'Copy shareable link',
        copySuffix: 'copy',
        country: 'Country',
        couponcode: 'Coupon code',
        coupons: 'Coupons',
        create: 'Create',
        createapikey: 'Create API key',
        created: 'Created',
        createdat: '@:labels.created at',
        createdon: '@:labels.created on',
        createfirstplan: 'Create your first plan',
        createfirsttaxrate: 'Create your first tax rate',
        createfirstupgrade: 'Create your first upgrade',
        createnewevent: 'Create new event',
        createneweventtype: 'Create new event type',
        createnewtype: 'Create new type',
        createnow: 'Create now',
        createplan: 'Create a plan',
        creator: 'Creator',
        credit: 'Credit',
        credits: 'Credits',
        creditsafterbooking: 'Credits remaining in this billing cycle after this booking',
        creditsbeforebooking: 'Credits available in this billing cycle before this booking',
        creditssummary: 'Credits summary',
        creditstotal: 'Total credits in this billing cycle',
        creditsused: 'Credits used',
        csvpreview: 'CSV preview ({count})',
        currency: 'Currency',
        currentplan: 'Current Plan',
        currentplanstart: 'Current plan started at',
        currentplanend: 'Current plan ends at',
        custom: 'Custom',
        customernumbersettings: 'Customer number settings',
        customersettings: 'Customer Settings',
        customfields: 'Custom Fields',
        customwebaddress: 'Bring own domain',
        deactivateDeleteCommunity: 'Deactivate/Delete Community',
        customise: 'Customise',
        customization: 'Customization',
        customize: 'Customize',
        customizeaplan: 'Customize a plan',
        customizebuiltindomain: 'Customize built-in domain',
        dashboard: 'Dashboard',
        dataprocessingaddendum:
            'Data Processing Addendum (DPA) and appendix technical and organizational measures (TOMs)',
        date: 'Date',
        dateempty: 'No date selected',
        dateoverride: 'Date override',
        datesandtimes: 'Dates and Times',
        day: 'Day | Days',
        daybookingconfiguration: 'Day booking configuration',
        days: 'Days',
        default: 'Default',
        defaultplan: 'Default plan',
        defaults: 'Defaults',
        defaultsforimportedevents: 'Defaults for imported events',
        delete: 'Delete',
        deletemethod: 'Delete method',
        deleteresource: 'Delete resource',
        deleterow: 'Delete row',
        deletetimepass: 'Delete Time Pass',
        deleteuser: 'Delete user',
        description: 'Description',
        descriptionoptional: 'Description (Optional)',
        details: 'Details',
        discard: 'Discard',
        discount: 'Discount',
        domain: 'Domain',
        doorsandkeys: 'Doors & Keys',
        download: 'Download',
        downloadcsvtemplate: 'Download CSV template',
        downloadGerman: 'Download German',
        downloadEnglish: 'Download English',
        draft: 'Draft',
        dragdropimage: 'Drag an image here or click to select.',
        duplicate: 'Duplicate',
        duration: 'Duration',
        durationhours: 'Duration (hours)',
        edit: 'Edit',
        editbookingresourcegroup: 'Edit booking resource group',
        editrow: 'Edit row',
        editrowwithnumber: 'Edit row {number}',
        editsetup: 'Edit setup',
        edittext: 'Edit text',
        email: 'Email',
        emailconfirmation: 'Email confirmation',
        emailfooter: 'Email Footer/Disclaimer',
        emailisconfirmed: 'Email is confirmed',
        emailstonotify: 'Emails to notify about booking',
        embed: 'Embed',
        embedcode: 'Embed code',
        enabled: 'enabled',
        enableintegration: 'Enable integration',
        end: 'End',
        enddate: 'End date',
        ended: 'Ended',
        endtime: 'End time',
        event: 'Event',
        eventFormat: 'Where does this event take place?',
        eventParticipation: 'How would you like to organize participation for this event?',
        eventcreate: '@:labels.create @.lower:labels.event',
        eventcreated: '@:labels.event @.lower:labels.created',
        eventdelete: '@:labels.delete @.lower:labels.event',
        eventedit: '@:labels.edit @.lower:labels.event',
        eventOngoing: 'Ongoing',
        events: 'Events',
        eventsall: 'All events',
        eventsnext: 'Next events',
        eventspast: 'Past events',
        eventsupcoming: 'Upcoming events',
        eventsupcomingpublic: 'Next public events',
        eventticketpayments: 'Event Ticket Payments',
        eventtime: 'Time',
        eventtypes: 'Event types',
        eventyour: 'your @.lower:labels.event',
        everyone: 'Everyone',
        excluding: 'excl.',
        exclusive: 'Exclusive',
        exit: 'Exit',
        expiration: 'Expiration',
        expiresAt: 'Expires at',
        externalregistrationlink: 'External registration link',
        externalregistration: 'External registration',
        externalbookingid: 'External booking ID',
        failedGeneric: '@:errors.failedGeneric',
        failedToSaveChanges: '@:errors.failedToSaveChanges',
        favicon: 'Favicon',
        features: 'Features',
        feed: 'Feed',
        female: 'Female',
        fileadd: 'Add file',
        files: 'Files',
        finish: 'Finish',
        firstinvoice: 'First invoice',
        firstthingsfirst: 'First things first',
        follow: 'Follow',
        follower: 'no followers | one follower | {n} followers',
        followerrole: 'Follower',
        followers: 'Followers',
        following: 'Following',
        followingday: 'Following day',
        free: 'free',
        freetrial: 'Free Trial',
        freeticket: 'Free ticket',
        from: 'From',
        fromprice: '@:labels.from',
        gender: 'Gender/Salutation',
        general: 'General',
        generalinformation: 'General Information',
        getstarted: 'Get started',
        getstartedwithtaxrates: 'Get started with tax rates',
        getstartedwithupgrades: 'Get started with upgrades',
        getticket: 'Tickets',
        going: 'Going',
        goingcount: 'is going | are going',
        googlecalendar: 'Google Calendar',
        gotosettings: 'Go to settings',
        gotosignupsettings: 'Go to signup settings',
        group: 'Group',
        groupby: 'Grouped by {subject}',
        groupmember: 'Group member | Group members',
        groupname: 'Group name',
        groupnew: '@:labels.new @.lower:labels.group',
        guest: 'Guest',
        hashtag: 'Hashtag',
        hashtagadd: '@:labels.add @.lower:labels.hashtag',
        headline: 'Headline',
        help: 'Help',
        helparticle: 'help article',
        helparticles: 'help articles',
        helpurl: 'Help URL',
        here: 'here',
        hidden: 'Hidden',
        hide: 'Hide',
        hideevent: 'Hide event',
        hideinupcominglists: 'Hide in upcoming lists',
        hour: 'hour | hours',
        hours: 'Hours',
        hourshort: 'h',
        id: 'ID',
        idealwero: 'Wero (iDEAL)',
        identityproviders: 'Identity providers',
        image: 'Image',
        imageadd: '@:labels.add @.lower:labels.image',
        imageguidelines: 'Image Guidelines',
        imageSection: 'Image Section',
        import: 'Import',
        imprint: 'Imprint',
        inactive: 'Inactive',
        inapp: 'In-app',
        including: 'incl.',
        inclusive: 'Inclusive',
        information: 'Information',
        integrations: 'Integrations',
        interested: "I'm interested",
        interestedpeople: 'Interested',
        interestedsent: 'Request sent',
        invalid: 'Invalid',
        invite: 'Invite',
        invited: 'Invited',
        invitedmembers: 'Invited members',
        invitemember: 'Invite member',
        invoice: 'Invoice',
        invoiceNumber: 'Invoice number',
        invoicenext: 'Next Invoice',
        invoicepreview: 'Invoice preview',
        invoices: 'Invoices',
        itemscount: 'no items | 1 item | {n} items',
        javascript: 'JavaScript',
        javascriptsnippet: 'JavaScript snippet',
        join: 'Join',
        keepBooking: 'Keep booking',
        key: 'Key',
        keyassign: 'Assign a @.lower:labels.key',
        keycreate: '@:labels.create a @.lower:labels.key',
        keyimport: 'Import',
        keymanagement: 'Key Management',
        keynotassigned: 'No @.lower:labels.key assigned',
        keys: 'Keys',
        language: 'Language',
        last4digits: 'Last four digits',
        lastactivity: 'Last activity',
        lastupdatedat: 'Last updated at',
        leave: 'Leave',
        legacy: 'Legacy',
        legacypricing: 'Legacy pricing',
        linkedbookingid: 'Linked booking ID',
        letsgetstarted: "Let's get started.",
        letstalkabout: "Let's talk about",
        limitUsersAlmostReached: 'Member limit almost reached',
        limitUsersReached: 'You have reached the member limit',
        lock: 'Lock',
        link: 'Link',
        linkadd: '@:labels.add @.lower:labels.link',
        linkcopy: '@:labels.copy @.lower:labels.link',
        linkInvalid: 'Invalid invitation link',
        linkInvalidDescription:
            'The link has expired, has been used before or has been deleted. Please try again. If the issue keeps happening, contact a Community Manager.',
        links: 'Links',
        list: 'List',
        loaded: 'loaded',
        loading: 'loading',
        loadmore: 'Load more',
        location: 'Location',
        locationcreate: '@:labels.create @.lower:labels.location',
        locationedit: '@:labels.edit @.lower:labels.location',
        locations: 'Locations',
        locks: 'Locks',
        pin: 'PIN',
        login: 'Log in',
        loginctatitle: 'Login CTA title',
        logout: 'Log out',
        kinescopeVideoPlayer: 'Kinescope video player',
        loomVideoPlayer: 'Loom video player',
        makeadmin: 'Make admin',
        male: 'Male',
        manage: 'Manage',
        managecustomervatsettings: 'Manage VAT settings',
        managesubscription: 'Manage subscription',
        markallasread: 'Mark all as read',
        markasfeatured: 'Mark as featured',
        markassticky: 'Mark as sticky',
        market: 'Market',
        matching: 'Matching',
        marketitem: 'Market item',
        marketitemclose: '@:labels.close @.lower:labels.marketitem',
        marketitemcreate: '@:labels.create @.lower:labels.marketitem',
        marketitemdelete: '@:labels.delete @.lower:labels.marketitem',
        marketitemedit: '@:labels.edit @.lower:labels.marketitem',
        marketitems: 'Market items',
        marketitemsempty: 'No market items created yet.',
        marketitemyour: 'your @.lower:labels.marketitem',
        matchingprofiles: 'Matching profiles',
        maxamount: 'Max. amount',
        maxbookableamount: 'Max. bookable amount',
        maxbookingduration: 'Max booking duration',
        member: 'Member | Members',
        memberadd: 'Add member',
        memberalreadymembership:
            "This user has a current and/or upcoming membership plan. Users with a valid plan cannot be added to another user's membership.",
        membercreate: 'Create member',
        members: 'Members',
        membership: 'Membership',
        membershipcreate: 'Create membership',
        membershipdelete: 'Delete membership',
        membershipedit: 'Edit membership',
        membershippayments: 'Membership payments',
        membershipUpdate: {
            noPlanSelected: 'No plan is selected',
            payForAnotherMember: 'Pay for another member',
            payingFor: '{name} is paying for',
            proration: 'Proration',
            prorationDescription:
                'Adjusts the billing amount to reflect the difference between the subscription update date and the billing cycle anchor date. Ensures that customers are only charged for the time they use the updated plan.',
            removePlan: 'Remove Plan',
            removePlanWarning:
                'This membership pays for other members (child plans). Removing this membership will automatically cancel all related child plans as soon as you save it.',
            selectAPlan: 'Select a plan',
            updateSubscription: 'Update subscription',
        },
        membershipupgrade: 'Upgrade membership',
        membershipupgrades: 'Membership upgrades',
        membershipyour: 'Your @.lower:labels.membership',
        membersmanage: 'Manage members',
        mentionsmo: 'Mention someone',
        message: 'Message',
        messagenew: '@:labels.new @:labels.message',
        messenger: 'Messenger',
        minutes: 'Minutes',
        newconversation: 'New conversation',
        minamount: 'Min. amount',
        minbookableamount: 'Min. bookable amount',
        minbookingduration: 'Min booking duration',
        mine: 'Mine',
        monetization: 'Monetization',
        monetizationCollection: {
            subscriptions: {
                billing: 'Billing',
                // the minimum time before the next billing cycle to for subscribers to cancel their subscription
                cancelnotice: 'Cancellation notice period',
                invoices: 'Invoices',
                minduration: 'Minimum contract term',
                monetization: 'Monetization',
                paymentmethods: '@:labels.paymentmethods',
                pricing: 'Pricing',
            },
        },
        month: 'Month',
        more: 'More',
        morecount: '{count} more',
        moreoptions: 'More options',
        my: 'My',
        mybookings: '@:labels.my @.lower:labels.bookings',
        myevents: '@:labels.my @.lower:labels.events',
        mypages: '@:labels.my @.lower:labels.pages',
        mypastevents: 'My past events',
        myticket: '@:labels.my @.lower:labels.ticket',
        mytickets: '@:labels.my @.lower:labels.tickets',
        myticketsfor: '@:labels.my @.lower:labels.tickets for "{event}"',
        mywallet: '@:labels.my @.lower:labels.wallet',
        name: 'Name',
        namefirst: 'First name',
        namelast: 'Last name',
        nameyoureventtype: 'Name your event type',
        net: 'Net',
        netprice: 'Net price',
        netpricepercycle:
            'Net price per cycle. To calculate gross price will be used Tax rate associated with this resource.',
        never: 'Never',
        new: 'New',
        next: 'Next',
        nextinvoice: 'Next invoice',
        numberofblockingslots: 'Blocking slots',
        no: 'No',
        generaladmission: 'General admission',
        noactivesubscription: 'No active subscription',
        nonbinary: 'Non-binary',
        none: 'none',
        nooptions: 'No options',
        noresults: 'No results',
        notavailable: 'n/a',
        notdefined: 'not defined',
        note: 'Note',
        noteadd: 'Add a note',
        notgrouped: 'Not grouped',
        notificationpreferences: 'Notification preferences',
        notifications: 'Notifications',
        notificationspause: 'Pause notifications',
        notificationspaused: 'Notifications paused',
        notifymewhen: 'Notify me when',
        notifymewhenasadmin: 'Me as an admin',
        notprovided: 'not provided',
        nuki: 'NUKI',
        numberoftickets: 'Number of Tickets',
        oauth2: 'OAuth 2.0',
        of: 'of',
        ok: 'OK',
        option: 'Option',
        options: 'Options',
        nooptionsavailable: 'No options available',
        optionsdisplay: 'Display options',
        or: 'or',
        ordercheckout: 'Checkout',
        ordercompletesubcription: 'Complete paid subscription',
        ordercompletesubcriptionfree: 'Complete free subscription',
        orderconfirm: 'Confirm order',
        orderreview: 'Review',
        ordersummary: 'Order summary',
        organizer: 'Organizer',
        organizers: 'Organizers',
        organizersadd: '@:labels.add @:labels.organizers',
        others: 'no one | one other | {n} others',
        overwriteprice: 'Overwrite price',
        owner: 'Owner',
        owners: 'Owners',
        ownersadd: 'Add owners',
        page: 'Page',
        pagecreate: '@:labels.create @.lower:labels.page',
        pagecreated: '@:labels.page @.lower:labels.created',
        pagedelete: '@:labels.delete @.lower:labels.page',
        pageedit: '@:labels.edit @.lower:labels.page',
        pagename: 'Page name',
        pagepublish: '@:labels.publish @.lower:labels.page',
        pagepublished: '@:labels.page @.lower:labels.published ',
        pages: 'Pages',
        pagesempty: 'No pages created yet.',
        pagesmy: '@:labels.mypages',
        pageyour: 'your @.lower:labels.page',
        paid: 'paid',
        purchased: 'Purchased',
        paidby: 'Paid By',
        participants: 'Participants',
        password: 'Password',
        passwordreset: 'Reset password',
        passwordsave: 'Save password',
        passwordsetnew: 'Set a new password',
        pause: 'Pause',
        pay: 'Pay',
        payforanothermember: 'Pay for another member',
        payment: 'Payment',
        paymentfees: 'Payment fees',
        paymentmethod: 'Payment method',
        paymentmethodadd: '@:labels.add @.lower:labels.paymentmethod',
        paymentmethoddelete: '@:labels.delete @.lower:labels.paymentmethod',
        paymentmethods: 'Payment methods',
        payments: 'Payments',
        per: 'Per',
        permissions: 'Permissions',
        permissionsEveryoneInCommunity: 'Everyone in the community',
        permissionsGroup: {
            approval: 'Approval required to join',
            inviteonly: 'Join by invitation only',
            open: 'Anyone can join',
            permissions: 'Permissions',
        },
        permissionsOnlyAdministrators: 'Only administrators',
        permissionsAdministratorsAndMembers: 'Admins and members',
        permissionsAllMembers: 'All members',
        permissionsMembersWithPlans: 'Members with specific plans',
        permissionsRule: 'Who can do this?',
        permissionsAddRule: 'Add rule',
        permissionsRemoveRule: 'Remove rule',
        permissionsCustomField: 'Custom field identifier',
        permissionsValues: 'Allowed values',
        permissionsValuesPlaceholder: 'Enter comma-separated values',
        permissionsActions: {
            eventsCreate: 'Who can create events?',
            pagesCreate: 'Who can create pages?',
            feedPost: 'Who can post in the feed?',
        },
        permissionsRules: {
            role: 'Administrators or member roles',
            plan_id: 'Membership plan',
            custom: 'Custom profile field',
            everyone: 'Everyone',
        },
        permissionsSeeAllAdmins: 'See all admins',
        permissionsWhoCanCreateEvents: 'Who can create events',
        phonenumber: '@:labels.phoneNumber',
        phoneNumber: 'Phone number',
        phonenumbernotprovided: '@:labels.phonenumber @:labels.notprovided',
        pinadd: 'Pin',
        pinremove: 'Unpin',
        placeholder: 'Placeholder',
        plan: 'Plan',
        plancancel: 'Cancel plan',
        plancancelledto: 'Plan cancelled to',
        planchange: 'Change plan',
        planchangecancel: 'Cancel plan change',
        planchoose: 'Choose plan',
        plancurrent: 'Your @.lower:labels.plan',
        plandetails: 'Plan details',
        planedit: 'Edit plan',
        planmanage: 'Manage plan',
        plannew: 'New @.lower:labels.plan',
        planreactivate: 'Undo cancellation',
        plans: 'Plans',
        planselection: 'Choose your plan',
        planupcoming: 'Upcoming plan',
        planupcomingstart: 'Upcoming plan starts at',
        planupdate: 'Plan update',
        planupgradechose: 'Select your plan upgrades',
        planupgrades: 'Plan upgrades',
        policies: 'Policies',
        policy: 'Policy',
        policyimport: 'Import',
        policynotassigned: 'No policy assigned',
        post: 'Post',
        postalcode: 'ZIP code',
        postbtn: '@:labels.post',
        postedit: '@:labels.edit @.lower:labels.post',
        postpin: 'Pin post',
        postpublished: '@:labels.post @.lower:labels.published',
        posts: 'Posts',
        postyour: 'your @.lower:labels.post',
        prefernottosay: 'Prefer not to say',
        preview: 'Preview',
        previous: 'Previous',
        paidAccessStartsHere: 'Exclusive content starts below',
        premiumAccessIncludedInYourPlan: 'Exclusive content is included in your plan',
        paywallSection: 'Paywall Section',
        price: 'Price',
        priceandlimits: 'Price and Limits',
        priceandterms: 'Price and Terms',
        pricebeforediscount: 'Price before discount',
        pricedisplay: 'Display price',
        pricegross: 'Gross',
        pricein: 'Price in {currency}',
        pricenet: 'Net',
        priceperday: 'Price per day',
        priceperhour: 'Price per Hour',
        priceperslot: 'Price per slot',
        pricepreview: 'Price preview',
        priceoverride: 'Price override',
        pricesubtotal: 'Subtotal',
        pricetotal: 'Total',
        pricevaries: 'prices vary',
        primarycolor: 'Primary color (brand color)',
        privacypolicy: 'Privacy Policy',
        private: 'Private',
        profile: 'Profile',
        profileedit: '@:labels.edit @:labels.profile',
        profilename: 'Profile name',
        profilepublish: '@:labels.publish @.lower:labels.profile ',
        profilepublished: '@.lower:labels.profile @:labels.published',
        profileupdated: '@:labels.profile @.lower:labels.updated',
        projects: 'Projects',
        provider: 'Provider',
        public: 'Public',
        publish: 'publish',
        published: 'published',
        push: 'Push',
        pushnotification: 'Push notification',
        quantity: 'Quantity',
        rate: 'Rate',
        rateinpercent: 'Rate in %',
        ratetype: 'Rate type',
        readhelparticle: 'Read the Help Article',
        readless: 'read less',
        readmore: 'Read more',
        redeem: 'Redeem',
        redeemable: 'Redeemable',
        redeemed: 'Redeemed',
        redirecturl: 'Redirect URL',
        refresh: 'Refresh',
        refund: 'Refund',
        refunded: 'refunded',
        refundedamount: 'Refunded amount',
        refundedat: 'Refunded at',
        refundUnusedMembershipTime: 'Refund unused time of the membership',
        region: 'Region',
        registrationadditionalinfo: 'Reg.: Additional info',
        registrationaddress: 'Registration address',
        registrationaddressedit: '@:labels.edit @.lower:labels.registrationaddress',
        registrationcity: 'Reg.: City',
        registrationcompany: 'Reg.: Company',
        registrationconfirmation: 'Registration confirmation',
        registrationcountry: 'Reg.: Country',
        registrationlink: 'Public registration link',
        registrationpostalcode: 'Reg.: Postal code',
        registrationstreet: 'Reg.: Street',
        registrationphone: 'Reg.: Phone',
        reject: 'Reject',
        remaining: 'left',
        remove: 'Remove',
        removeapikey: 'Removing api key',
        renewsannually: 'Renews annually',
        renewsmonthly: 'Renews monthly',
        renewsubscription: 'Renew your subscription to continue using coapp.',
        reopen: 'Reopen',
        reopenItem: 'Reopen item',
        reply: 'Reply',
        reportbug: 'Report a bug',
        requestapprovalfromyourmembers: 'Request approval from your members',
        required: 'Required',
        reset: 'Reset',
        resolve: 'Resolve',
        resource: 'Resource',
        resourcecategories: 'Resource categories',
        resourceid: 'Resource ID',
        resourcename: 'Resource name',
        resources: 'Resources',
        revenue: 'Revenue',
        review: 'Review',
        revokeadminrights: 'Revoke admin rights',
        role: 'Role',
        rightOfWithdrawalForm: 'Withdraw from contract',
        roles: {
            admin: 'Admin',
            user: 'User',
        },
        rules: {
            add_condition: 'Add condition',
            add_group: 'Add rule',
            conditions: {
                AND: 'And',
                OR: 'Or',
            },
            fields: {
                plan_id: 'Plan',
                user_email: 'User email',
                user_id: 'User',
                user_role: 'User role',
                user_status: 'User status',
            },
            operators: {
                ANY: 'is any',
                BETWEEN: 'is between',
                EQ: 'is equal to',
                GT: 'greater than',
                GTE: 'greater than or equal to',
                LIKE: 'contains',
                LT: 'less than',
                LTE: 'less than or equal to',
                NEQ: 'is not equal to',
                NOTLIKE: 'does not contain',
            },
            remove_condition: 'Remove condition',
        },
        run: 'Run',
        runprogress: 'Run progress',
        salutation: 'Salutation',
        salutationfemale: 'Ms.',
        salutationmale: 'Mr.',
        salutationnonbinary: 'Mrx.',
        save: 'Save',
        saveandpublish: 'Save and Publish',
        saveasdraft: 'Save as Draft',
        saveasicsfile: 'Save as ICS',
        saveChanges: 'Save Changes',
        saved: 'saved',
        schedulecancellation: 'Schedule cancellation',
        scopes: 'Scopes',
        scrolltotop: 'Scroll to top',
        search: 'Search',
        secondarycolor: 'Secondary color (links color)',
        seeall: 'See all',
        seemore: 'See more',
        select: 'Select',
        selectaccountcalendar: 'Select account that has access to your calendar',
        selectall: 'Select all',
        selectcalendartosync: 'Select calendar to sync with',
        selected: 'Selected',
        selectcolumns: 'Select columns',
        selectedupgrade: 'Selected upgrade',
        selectlock: 'Select locks',
        selectlockDescription:
            'You can select one or more locks and define how long access is available before the booking starts and after it ends.',
        selectplan: 'Select plan',
        userFilters: {
            after: 'is after',
            before: 'is before',
            between: 'is between',
            clearFilters: 'clear filters',
            equal: 'is equal',
            fieldPlaceholder: 'Select field',
            filterField: 'Field',
            from: 'From',
            greaterThan: 'is greater than',
            greaterThanOrEqual: 'is greater than or equal',
            inUsersValues: 'is any of',
            lessThan: 'is less than',
            lessThanOrEqual: 'is less than or equal',
            like: 'contains',
            filter: 'Filter',
            addFilter: 'Add filter',
            notEqual: 'is not equal',
            notInUsersValues: 'is none of',
            notLike: 'does not contain',
            operator: 'Operator',
            operatorPlaceholder: 'Select operator',
            planPlaceholder: 'Select plan',
            statusPlaceholder: 'Select status',
            to: 'To',
            value: 'Value',
            valuePlaceholder: 'Enter value',
        },
        selectresource: 'Select a resource',
        selectresources: 'Select resources',
        send: 'Send',
        sendfeaturerequest: 'Send feature request',
        sendinvoice: 'Send invoice',
        sepa: 'SEPA',
        sepaaccount: 'SEPA account',
        sepadirectdebit: 'SEPA direct debit',
        sepaHelpArticleTitle: "'Important considerations when accepting SEPA direct debit payments for memberships'",
        sepapayments: 'SEPA payments',
        setAsDefault: 'Set as Default',
        setcreditsamountoptional: 'Set credits amount (optional)',
        setmaxbookableamount: 'Set max. bookable amount',
        setminbookableamount: 'Set min. bookable amount',
        settings: 'Settings',
        setuppayments: 'Setup payments',
        sameday: 'Same day',
        share: 'Share',
        shareableregistrationlink: 'Shareable registration link',
        shareinfeed: 'Share in feed',
        showdetails: 'Show Details',
        showmore: 'Show more',
        signup: 'Sign up',
        suggestedChats: 'Suggested chats',
        signupApproval: 'Approval based',
        signupctatitle: 'Sign Up CTA Title',
        signupInviteOnly: 'By invitation only',
        signupOpen: 'Anyone can sign up',
        signupstrategies: 'Sign-up Strategies',
        signupstrategy: 'Sign Up Strategy',
        skip: 'Skip',
        slots: 'Slots',
        slug: 'Slug',
        sold: 'Sold',
        soldout: 'Sold out',
        sortbyselected: 'Sort by selected',
        space: 'Space',
        spacecolors: 'Space colors',
        spaceid: 'Space ID',
        spaceintroduction: 'Space introduction',
        spacelogo: 'Space logo',
        start: 'Start',
        startdate: 'Start date',
        startsync: 'Start sync',
        starttime: 'Start time',
        statistics: 'Statistics',
        status: 'Status',
        step: 'Step {number}',
        stripe: 'Stripe',
        stripeaccount: 'Stripe account',
        stripecustomer: 'Stripe customer',
        stripedashboard: 'Stripe dashboard',
        stripepaymentfees: 'Stripe Payment fees',
        stripepaymentmethods: 'Stripe payment methods',
        strongtags: 'Strong Tags',
        strongtagssuggestion: 'Strong tags suggestion',
        submit: 'Submit',
        subscribenow: 'Subscribe now',
        subscriptioncancelssoon: 'Your subscription will cancel soon',
        subscriptioncreate: 'Create subscription',
        subscriptionend: 'Subscription end',
        subscriptionpreview: 'Subscription preview',
        subscriptionSchedule: {
            addFreeTrial: 'Add free trial',
            anchorDate: 'Anchor date',
            automaticCharge: "Automatically charge the user's standard payment method",
            billingCycle: 'Billing cycle',
            configurationQuestion: 'When do you want this configuration to take effect?',
            customDate: 'Custom date',
            emailInvoice: 'Email invoice to the customer to pay manually',
            endOfBillingCycle: 'End of billing cycle',
            endOfBillingCycleWithCancellation: 'End of billing cycle, considering cancellation period',
            forever: 'Forever',
            freeTrialDays: 'Free trial days',
            now: 'Now',
            paymentCollectionMethod: 'Payment collection method',
            resetBillingCycle: 'Reset billing cycle',
            resetBillingCycleDisabled: 'Billing cycle cannot be reset while on an active trial',
            resetBillingCycleInfo:
                'If activated, the anchor date for the billing cycle will reset on the date this update becomes effective.',
            scheduleUpdate: 'Schedule update',
            startEndDate: 'Start and end date',
            title: 'Subscription schedule',
            trialEndsAt: 'Trial ends at',
        },
        subscriptionstart: 'Subscription start',
        subscriptionstatus: 'Subscription status',
        subtitle: 'Subtitle',
        suggestedcontent: 'Suggested content',
        summary: 'Summary',
        suppresswelcomeemail: 'Suppress welcome email',
        support: 'Support',
        suspensionreason: 'Suspension reason',
        synchronize: 'Synchronize',
        synchronizecalendar: 'Synchronize calendar',
        systemid: 'System ID',
        systemuserid: '{systemname} System User ID',
        tags: 'Tags',
        tax: 'Tax',
        taxrate: 'Tax rate',
        taxrates: 'Tax Rates',
        taxtype: 'Tax type',
        terms: 'Terms',
        termsandconditions: 'Terms and Conditions',
        termsandconditionslink: 'Link to Terms and Conditions (Optional)',
        termsofservice: 'Terms of Service',
        testconnection: 'Test connection',
        text: 'Text',
        textcolor: 'Text color',
        textSection: 'Text Section',
        theeventscalendar: 'The Events Calendar',
        thisevent: 'This event',
        ticket: 'Ticket',
        ticketcode: 'Ticketcode',
        ticketcreate: 'Create ticket',
        ticketedit: 'Edit ticket',
        tickets: 'Tickets',
        ticketsmore: 'More tickets',
        ticketstatus: 'Ticket status',
        ticketurl: 'Ticket link',
        time: 'Time',
        timepass: 'Time pass',
        timepassCard: {
            currentlyActive: 'Currently active',
            left: '{count} left',
            useConfirmation: 'If you proceed this pass will be activated immediately and redeemed. Are you sure?',
            useNow: 'Use now',
        },
        timepasses: 'Time Passes',
        times: 'time | times',
        timezone: 'Timezone',
        title: 'Title',
        to: 'To',
        today: 'Today',
        tokenurl: 'Token URL',
        topics: 'Topics',
        total: 'Total',
        tryagain: 'Try again',
        type: 'Type',
        unarchive: 'Unarchive',
        unavailable: 'unavailable',
        undocancellation: 'Undo cancellation',
        unfollow: 'Unfollow',
        unit: 'Unit',
        unknown: 'Unknown',
        unlock: 'Unlock',
        unsavedchanges: 'Unsaved changes',
        update: 'Update',
        updatecreate: '@:labels.create @.lower:labels.update',
        updated: 'Updated',
        updatedat: 'Updated at',
        updatedelete: '@:labels.delete @.lower:labels.update',
        updatedon: '@:labels.updated on',
        requestPins: 'Request PINs',
        updateedit: '@:labels.edit @.lower:labels.update',
        updates: 'Updates',
        usedcashcredits: 'Used cash credits',
        usedtimecredits: 'Used time credits',
        upgrade: 'Upgrade',
        upgradecoappsubscription: 'upgrade your coapp subscription',
        upgrades: 'Upgrades',
        upgradeSubscription: 'Upgrade Subscription',
        upgradetounlock: 'Upgrade to unlock',
        uploadcsv: 'Upload CSV',
        uploadnewpicture: 'Upload new picture',
        user: 'User',
        userinfourl: 'User Info URL',
        username: 'Username',
        usernotavailable: 'Not available',
        usershowmatchingprofiles: 'Show matches',
        userssome: 'Some users',
        valid: 'Valid',
        validfor: 'Valid for',
        validfrom: 'Valid from',
        validminutesafterbooking: 'Allow access after the booking ends (in minutes)',
        validminutesbeforebooking: 'Allow access before the booking starts (in minutes)',
        validto: 'Valid to',
        validuntil: 'Valid until',
        expired: 'Expired',
        vat: 'VAT',
        vatid: 'VAT ID',
        vatidnotprovided: 'VAT ID not provided',
        versionfrom: 'Version from',
        view: 'View',
        viewcurrentpaymentmethods: 'View current payment methods',
        vieweventdetails: 'View event details',
        viewprofile: 'View profile',
        viewswitcher: 'View switcher',
        viewupgradeoptions: 'View upgrade options',
        visibility: 'Visibility',
        visible: 'Visible',
        videoSection: 'Video Section',
        videoUrl: 'Video URL',
        tiktokVideoPlayer: 'TikTok video player',
        vimeoVideoPlayer: 'Vimeo video player',
        mixcloudAudioPlayer: 'Mixcloud audio player',
        podigeeAudioPlayer: 'Podigee podcast player',
        soundcloudAudioPlayer: 'SoundCloud audio player',
        spotifyAudioPlayer: 'Spotify audio player',
        wallet: 'Wallet',
        warning: 'Warning',
        week: 'Week',
        welcomeimage: 'Welcome image',
        welcometocoapp: 'Welcome to coapp',
        welcometocommunity: 'Welcome to the Community!',
        whocanbookresource: 'Who can book this resource',
        whocanseeresource: 'Who can see this resource',
        year: 'Year',
        yes: 'Yes',
        you: 'You',
        yourreply: 'Your reply',
        youtubeVideoPlayer: 'YouTube video player',
        zapier: 'Zapier',
    },
    languages: {
        de: 'German',
        en: 'English',
    },
    messages: {
        // Help text and loading feedback for legal and compliance settings.
        withdrawalNotificationAdmins:
            'Administrators receive notifications when a new withdrawal form is submitted, unless they have disabled these notifications in their notification preferences.',
        settingsPurchaseCancellationPolicy:
            'This text appears on the @:labels.purchaseCancellationPolicy page. Explain how buyers can withdraw from a purchase and where to send their request. Enabling this policy adds links in the public footer and the More menu in member navigation. Enable the purchase withdrawal form separately to let buyers submit requests online.',
        settingsPurchaseWithdrawalForm:
            'The public withdrawal form lets people submit a withdrawal request for a purchase. It is linked as @:labels.contractWithdrawal in the public footer when the withdrawal form is enabled. Submitters verify their email address and receive a confirmation of receipt; community administrators process the request manually.',
        agreementLoadFailed: 'Failed to load agreement',
        accessIdentityLabel: 'Access #{id}',
        accessKeyLabel: 'Key: {name}',
        contactAdminForPaidTickets:
            'Paid tickets can only be set up by admins. Contact an admin if you need to charge for this event.',
        nonAdminFreeTicketsOnly:
            'You can create free tickets here. If this event needs paid tickets, please contact an admin.',
        accountCannotBeDeleted: 'This account cannot be deleted because it is the admin of this platform.',
        accountDetailsUpdated: 'Account details changed',
        accountDetailsUpdateFailed: 'Could not update account details.',
        shareInMessengerFailed: 'Sharing in messenger failed.',
        shareInMessengerSuccess: 'Shared in messenger.',
        accountexists: 'Already have an account?',
        adminonlyfeature:
            'This feature is only available for admins. Please contact your administrator if you want to use it.',
        apikeynote: "NOTE: The key won't be shown again.",
        applicationFeeDefault: 'coapp charges a platform fee as specified in your subscription.',
        applicationFeePercent: 'coapp charges a platform fee fee of {percentage}% on all revenue processed.',
        approvalDomainHint:
            'Optional: Email addresses with the following domain will be automatically approved after they have confirmed their email address',
        auth: {
            accountNotFound: 'There’s no account associated with {email} in this community.',
            accountNotFoundTitle: 'Account not found',
            additionalVerificationRequired:
                'This account needs an additional verification step before login can finish.',
            codeSentInfo: 'We sent a verification code to {email} address.',
            continueWithEmailCode: 'Continue with email code',
            continueWithPassword: 'Continue with password',
            expiredOrInvalidSession: 'The login session is invalid or has expired.',
            expiredOrInvalidInput: 'The entered login information is invalid or has expired.',
            invalidCredentials:
                'This combination of email and password does not exist. Please double-check and try again.',
            invalidInput: 'Check the entered information and try again.',
            invalidOtp: 'The verification code is invalid or has expired.',
            invalidSession: 'The login session is invalid or has expired.',
            loggingOut: 'Signing you out and clearing the current session.',
            otpRequired: 'Enter the verification code to continue.',
            passwordPrompt: 'Enter the password for {email} to continue.',
            resendCode: 'Resend code',
            serviceUnavailable: 'Login is temporarily unavailable. Please try again.',
            sendCode: 'Send code',
            unauthenticated: 'Please log in to continue.',
            unexpectedError: 'Something went wrong. Please try again.',
            useDifferentEmail: 'Use a different email?',
            useEmailCode: 'Use a one-time code',
            usePassword: 'Use password instead',
            verificationCodeLabel: 'Verification code',
            verificationCodePlaceholder: 'Enter your code',
            verifyCode: 'Verify code',
        },
        authIdentityProviders: 'Connect your existing community',
        authMethodCreateFailed: 'Could not create auth method',
        authMethodUpdateFailed: 'Could not update auth method',
        authSignUpStrategy: 'Choose how users can sign up to your community',
        authurlhelp: 'Endpoint to get the authorization code',
        billingAddressUpdated: 'Billing address updated',
        billingAddressUpdateFailed: 'Could not update billing address. Please try again later.',
        billingAdminDataLoadFailed: "Can't get billing admin data",
        captchaReady: 'Human verification complete.',
        captchaWorking: "Verifying you're human...",
        humanVerificationError: 'Human verification could not be completed. Please reload the page and try again.',
        maxTicketsReached: 'You can select up to {max} tickets in total.',
        billingAdminTooltip:
            'The community owner is the admin who created the community and is in charge of all invoices and billing-related communications.',
        blockingRuleResourceExists: 'This resource is already present in blocking rules',
        blockingRulesHelp:
            'When this resource is booked, the following resources are also blocked for the same period.',
        blockingRuleSlotsHelp: 'Number of slots to be blocked for this resource.',
        booking: {
            adminBookingTitle: '{resource} booking',
            adminBookingTitleForUser: '{resource} booking for {name}',
            cancelationRefundDetails: `This will cancel your '{resource_name}' booking for {date}.<br>- You will receive a confirmation email<br>- If you paid for this booking, you will get a full refund`,
            cancelationRefundDetailsForAdmin: `This will cancel the '{booking_title}' for {date}.<br>
- The user will be automatically notified of the cancellation<br>
- If this was a paid booking, a refund will be processed<br>
- If this booking was added to a finalized membership invoice, no refund will be issued`,
            cancellationinfo:
                'Booking cancellation less than {hours} hours before the event start time is not allowed.',
            cancelSuccess: 'Booking was canceled.',
            cancelWarning: 'Cancel booking',
            cancelWarningForAdmin: `Cancel '{booking_title}'?`,
            capacityWarning:
                'The resource capacity for the selected time slot is either fully booked or outside the available hours. Do you still want to proceed with the booking?',
            capacityWarningConfirmation: 'Yes, I understand and want to proceed.',
            error: 'Unfortunately something went wrong while creating your booking.',
            errorPastTime: 'You cannot book a resource in the past.',
            lockActionLockTitle: 'Lock {lock_name}. Available from {available_from} until {available_until}.',
            lockActionLockFailed: 'Could not lock this booking lock.',
            lockActionLockSuccess: 'Lock command sent successfully.',
            lockActionUnlockTitle: 'Unlock {lock_name}. Available from {available_from} until {available_until}.',
            lockActionUnlockFailed: 'Could not unlock this booking lock.',
            lockActionUnlockSuccess: 'Unlock command sent successfully.',
            lockAvailability: 'PINs and lock controls are available from {available_from} until {available_until}.',
            lockAvailabilityActiveUntil: 'You can open the locks until {available_until}.',
            lockAvailabilityCountdown: 'You can open the connected locks in {time_until_available} ({available_from}).',
            lockAvailabilityExpired: 'It is no longer possible to use the locks.',
            lockAvailabilityStartsAt: 'You can open the connected locks from {available_from}.',
            lockAvailabilityUnitHour: '{count} hour | {count} hours',
            lockAvailabilityUnitMinute: '{count} minute | {count} minutes',
            lockAvailabilityUnitSecond: '{count} second | {count} seconds',
            lockPinsRequestFailed: 'Could not request booking PINs.',
            lockPinsRequested: 'Booking PINs were requested successfully.',
            lockPinsRequestTitle:
                'Request the PIN for {lock_name}. Available from {available_from} until {available_until}.',
            nobookings: "You don't have any upcoming bookings. You can create one right now!",
            processing: 'Your booking is being processed.',
            startBeforeEnd: 'The start time must be before the end time.',
            success: 'Your booking was successful.',
            timeRange: 'The booking times for this day are from {from} to {to}.',
        },
        bookingCategoryCreated: 'Booking category created',
        bookingCategoryCreateFailed: 'Could not create booking category',
        bookingCategoryDeleted: 'Booking category deleted',
        bookingCategoryDeleteFailed: 'Could not delete booking category',
        bookingCategoryUpdated: 'Booking category updated',
        bookingCategoryUpdateFailed: 'Could not update booking category',
        bookingEmailPinPlaceholder: 'User placeholder for linked lock pin code:',
        bookingEmailPlaceholdersIntro: 'Placeholders',
        bookingEmailPlaceholdersOutro: 'will be replaced by actual booking times.',
        bookingEmailPlaceholdersTo: 'and',
        bookingNotifyEmailsHelp: 'To these emails will be sent booking infomation and user contact data.',
        bookingSystemDisabled:
            'The booking system is currently disabled. Please enable it in the settings so that it can be used by your members.',
        bookWholeSlotHelp:
            'If activated, min and max duration will be ignored and the resource is booked for whole booking slot available within the day of the booking.',
        allowStartedTimeSlotBookingHelp:
            'If activated, members can still book after the booking start time has passed. Example: if check-in starts at 11:00 and it is now 13:30, the booking can still be made with a start time of 11:00. This also applies to shorter intervals, for example with 30-minute booking intervals a booking can still start at 14:00 when it is already 14:15.',
        builtindomaininfo: 'This is your built-in domain. Your community can always be accessed from this domain.',
        customwebaddressinfo: 'Bring your own domain for your community. Our support team will help you set it up.',
        bulkCreateKeepTabOpen: 'Please keep this tab open while users and memberships are being created.',
        bulkCreateMaxRows: 'Max 100 rows per run. Keep this tab open until the process finishes.',
        bulkCreateRequiredColumns:
            'Required columns: Email, First Name, Last Name, Plan ID or Plan Name. Optional: Company, Extras IDs, Extras Names, Suppress Welcome Email, Salutation.',
        buyMoreSeats:
            'Need more members, additional admin seats, or a custom Enterprise solution? Our sales team is here to help you find the right fit. {0}.',
        deactivateCommunityInfo:
            'If you want to deactivate or permanently delete your community, {0}. Our support team will help you with the next steps.',
        buyPass: {
            availableImmediately: 'Time pass will be available immediately.',
            overrideInfo: 'If enabled, the member will not be charged for this time pass.',
        },
        calendarLastSync: 'Last synchronization',
        calendarSubscribed: 'Coapp is subscribed to this calendar changes.',
        calendarSubscriptionExpires: 'Subscription expiration date time',
        calendarSyncBookingsToCalendar:
            'All upcoming bookings made in coapp will be copied to selected google calendar.',
        calendarSyncConnectHelp:
            'To enable calendar synchronization, you need to connect your account to your calendar.',
        calendarSyncEventsToCoapp: 'All upcoming events from google calendar will be copied to coapp.',
        calendarSyncSaveChanges:
            'Please save other changes made in this resource before starting synchronization with google calendar.',
        calendarSyncSaveAndStart: 'All changes will be saved and the initial synchronization will be started.',
        calendarSyncStartFailed: 'Failed to start calendar synchronization.',
        calendarSyncStarted:
            "Synchronization started, depending on amount of events it might take few minutes. It's safe to close tab.",
        cancellationPeriodHelp: 'Prevent members from cancelling bookings within x hours before a booking starts.',
        cancelUpcomingPlanDescription:
            "The upcoming plan change will be aborted so that user's current subscription does not change.",
        capacityHelp: 'How many users can book this at the same time',
        paymentMethodsAvailabilityStripeNotice:
            'There must be activated payment methods available in Stripe before you can enable them here.',
        cf: {
            // custom fields related messages
            createdSuccessfully: 'Custom field created successfully.',
            creationFailed: 'Failed to create custom field.',
            deletedSuccessfully: 'Custom field deleted successfully.',
            deletewarning:
                'Deleting this custom field will also delete all associated data from member profiles. This action cannot be undone. Please be certain.',
            updatedSuccessfully: 'Custom field updated successfully.',
            updateFailed: 'Failed to update custom field.',
        },
        changesApplyImmediately: 'The changes will be applied immediately.',
        changessaved: '@:labels.changes @.lower:labels.saved',
        channel: {
            saved: 'Channel saved',
            saveError: 'Error while saving the channel name',
        },
        chat: {
            groupnameempty: 'Group name cannot be empty.',
            leavechatconfirm: 'Are you sure you want to leave this chat?',
            nomembers: 'No members in this chat.',
        },
        clientidhelp: 'Client ID of the provider',
        clientsecrethelp: 'Client secret of the provider',
        closedForBookingsHelp: 'If set, does not allow bookings on date',
        cofile: {
            uploadFailed: 'Unable to upload file. Please try again later. Max file size 500 MB.',
            uploadTooLarge: 'The file you tried to upload is too large. Max 500 MB.',
        },
        communityNameHelp: 'The name of your community',
        communitySuspendedTitle: 'Your community has been suspended',
        configureaccesspolicies: 'Configure space access policies (user groups)',
        confirmationcodeinfo: 'Didnt receive a code? Check you spam folder, or try a different email address',
        confirmationcodesentotemail: 'Enter the confirmation code we sent to {email}',
        confirmStatusChange: "Are you sure you want to change the user's status to '{status}'",
        congratulations: 'Congratulations',
        connectauthprovider: 'Connect a provider for the authentication',
        connectionFailed: 'Connection failed',
        connectionSuccessful: 'Connection successful',
        connectionTestFailed: 'Failed to test the connection',
        connectStripe: 'Connect Stripe to start accepting payments',
        contactBillingManager: 'Contact your billing manager {name}',
        cookiebanner:
            'We use cookies and similar storage technologies that are necessary for this application to function. View the {privacypolicy}.',
        copiedtoclipboard: 'Copied to clipboard',
        copyright: '© 2025 coapp GmbH. All rights reserved.',
        copyshareablelink: 'Copy shareable link',
        countriesLoadFailed: 'Failed to load countries',
        createApiKeyConfirm: 'Are you sure to create a new api key ?',
        creatingapikey: 'Creating api key....',
        currencyDescription: 'Define the currency that will be used in the pricing of plans, upgrades, and resources.',
        currencyForPlan: 'Currency for plan',
        currencyForTimepass: 'Currency for time pass',
        currencyListLoadFailed: 'Could not get currency list.',
        currencySettingsLoadFailed: 'Failed to get space currency settings.',
        customerNumbersBackfilled: 'Successfully backfilled customer numbers',
        customerNumbersBackfillFailed: 'Failed to backfill customer numbers',
        customerPortalUrlFailed: 'Failed to generate customer portal url. Please contact support.',
        customerPortalUrlMissing: 'No valid customer portal URL found. Please contact support.',
        billingStatusLoadFailed: 'Failed to load billing status. Please contact support.',
        defaultplanused: 'Default plan will be used: {name}',
        deleteApiKeyWarning:
            "Once you delete this api key type, you won't be able to use it anymore in your application.",
        deleteEventConfirm: "Delete '{title}'?",
        deleteEventWarning: 'Once you delete an event, there is no going back. Please be certain.',
        deleteImageHeaderWarning: 'Are you sure you want to delete this image?',
        deleteResourceWarning: 'Once you delete a resource, there is no going back. Please be certain.',
        deleteTimepassWarning: 'Once you delete this time pass, there is no going back. Please be certain.',
        deleteUserConfirm: 'Are you sure you want to delete {name}?',
        deletewarning: 'Once you delete this, there is no going back. Please be certain.',
        deletewarningchannel: 'Deleting a channel cannot be undone. Are you sure you want to delete this channel?',
        domainalreadytaken: 'This domain already taken.',
        domainnamerules:
            'A domain name can be up to 63 characters (letters, numbers or combination) long.<br />The only symbol character domain names can include is a hyphen (-) although the domain name cannot start or end with a hyphen nor have consecutive hyphens.',
        downloadListCsv: 'Download list as CSV file',
        durationMustBeSet: 'You have to set a duration.',
        emailconfirmation: {
            confirmationsuccess: 'Email confirmation successful!',
            gotocommunity: 'Go to community',
            invalidcode: 'The confirmation code is invalid or has expired.',
            wasverified: 'Your email was verified.',
        },
        emailFooterHelp: 'This text will be inserted in the footer of all emails that are sent from your community.',
        enterconfirmationcode: 'Enter confirmation code',
        event: {
            attendanceCancellation: 'Are you sure about this? This event would be much nicer with you!',
            deleteWithParticipants:
                "This event has participants and can't be deleted. Set it to Draft to hide it instead.",
            ticketPurchaseSuccess: 'Your ticket purchase was successful!',
            ticketRefund: 'Refund',
            ticketRefundTitle: 'Ticket cancellation and refund',
            billingAddressInfo:
                'This billing address will be used for all future invoices. You can change it at any time.',
            linkvisibleforattendees: 'Link visible for attendees',
            ticketIsNotRefundable: 'This ticket cannot be refunded.',
            ticketRefundDescription:
                'The tickets will be cancelled immediately. Refunds for paid tickets may take up to 5-7 business days.',
            ticketRefundError: 'An error occurred while processing your refund. Please try again.',
            ticketRefundSuccess: 'Your ticket has been successfully canceled and refunded.',
            externalregistrationrequired: 'External registration is required to attend this event.',
        },
        eventcalendarintegrationpaywall:
            'The Events Calendar integration is available on paid plans. Upgrade to sync your events to WordPress.',
        eventcalendarloadfailed: 'Could not load The Events Calendar settings',
        eventcalendarsyncenabled: 'Turn on to sync your coapp events with WordPress.',
        eventCreateFailed: "Couldn't create event",
        eventSystemDisabled:
            'Events are currently disabled. Please enable them in the settings so that members can create and access events again.',
        pagesSystemDisabled:
            'Pages and channels are currently disabled. Please enable them in the settings so that members can create and access pages again.',
        faviconSizeHint: 'Favicon should be 32x32 pixels',
        featuresBookings:
            'Enable bookings to make rooms, equipment, services, or coaching directly bookable within your community. You determine for which plans offerings are available and whether they can be booked for free or for a fee.',
        featuresChannels: 'Channels provide order - create them for any project, topic or team',
        featuresPages:
            'Enable channels & pages for groups, collaboration, interaction, and knowledge sharing. Organize pages within channels and determine who can create, view, or join them. This way, open areas, private groups, or exclusive content can emerge.',
        featuresEvents:
            'Enable events to organize them directly within your community. You decide whether they are accessible only to members or also publicly, and you handle tickets and payments directly through it. The event feed provides space for updates, interaction, and surveys.',
        featuresHelpLink: 'Learn more about {feature}',
        featuresMatching:
            'Enable matchmaking to connect members based on your own criteria. Define what makes a good match through custom fields. A live score shows suitable contacts – without external AI models and with full control over your data.',
        featuresMarket:
            'Give members a shared marketplace where they can publish, discover, and respond to community listings.',
        featuresMarketDisableTitle: 'Disable Market?',
        featuresMarketDisableWarning:
            'The Market feature has been discontinued. If you disable it, it will be removed from your community and cannot be reactivated. This action cannot be undone.',
        fileTooLarge: 'The file you tried to upload is too large. Max {size}.',
        finalPriceForCustomer: 'Final price for customer will be',
        giveitaname: 'Give it a name',
        googleCalendarAuthUrlError: 'Error while getting Google Calendar auth url.',
        guest: {
            notamemberyetbody:
                'As a guest, you can only view this event. Become a member to discover what else your community has to offer. ',
            notamemberyettitle: 'Not a member yet?',
            notmemberyetconfirmtitle: 'Become a member',
            notmemberyetdiscardtitle: 'Stay on event page',
        },
        helpCenterSuffix: 'in our Help Center.',
        helpUrlDescription:
            'Define an URL under which your users can find help articles or a way to contact your customer support. This can be an external URL or a page or channel in your community. The link to this URL will show up as "Help" item in the dropdown menu when users click on their profile picture.',
        hiddenResourceHelp: 'If set, this resource will not be visible to users. Only admins will be able to see it.',
        hideeventdesc: 'Keep imported events hidden until you publish them.',
        hideupcomingevents: 'Set imported events as hidden in upcoming widgets.',
        imageguidelines:
            'Please avoid having text inside your image. The recommended image aspect ratio is 3x2 and the recommended image size is 1800x1200.',
        imageMaxSize2mb: 'Image, max file size 2MB.',
        imageUploadError: 'An error occurred while uploading the image. Please try again.',
        imageUploadFailed: 'Could not upload one or more images, please try again or check details',
        imageUploadSizeError: 'The image is too large. Please choose a smaller image. Max 10 MB.',
        inminutes: 'In minutes',
        integrationsEventCalendar: 'Sync your coapp Events with WordPress',
        integrationsGoogleCalendar: 'Connect and sync booking resources',
        integrationsNuki: 'Connect your Nuki Account',
        integrationsZapier: 'Connect coapp community to zapier',
        invaliddomain: 'Invalid domain.',
        javascriptIntro:
            'Your custom JavaScript will be placed inside the <code>&lt;head&gt;</code>-Tag on all pages.<br /><small>That way you can use third-party software for customer support such as Intercom and FreshDesk, web analytics like Google Analytics, Matomo and many other tools.</small>',
        javascriptNoScriptTags: 'type in without <script> tags',
        javascriptPlaceholders:
            'Placeholders <code v-pre>{{ name }}</code>, <code v-pre>{{ user_id }}</code>, <code v-pre>{{ user_status }}</code> and <code v-pre>{{ email }}</code> can be used to prefill user data in you custom code. All placeholders will be inserted inside <code>" "</code> to represent their correct Type (String) in JavaScript. When the user is unknown i.e. on sign up pages the value for all placeholders is <code>null</code>',
        legal: {
            termsandprivacydisclaimer:
                'By using this platform, you agree to our {0} and confirm that you have read our {1}.',
        },
        withdrawalEmailHint: 'Enter a valid email address for the confirmation email.',
        withdrawalFormIntro:
            'Send your withdrawal request with the details below. We automatically confirm receipt by email, forward the request to the community admins, and they process it manually.',
        withdrawalRequestError: 'Your withdrawal request could not be sent. Please check your details and try again.',
        withdrawalRequestSuccess:
            'Your withdrawal request has been received. We will send a confirmation email to the address provided.',
        otpCaptchaExpired: 'Human verification has expired. Please complete it again before requesting a new code.',
        otpCodeResent: 'A new confirmation code was sent.',
        otpRateLimited: 'Too many attempts. Please wait a moment and try again.',
        otpResendFailed: 'A new confirmation code could not be sent. Please try again.',
        otpResending: 'Sending a new code…',
        otpVerificationFailed: 'The confirmation code could not be verified. Please try again.',
        otpVerificationInstructions:
            'Check the email we sent to {email} and enter the code. Please check your spam folder too.',
        otpVerifying: 'Verifying code…',
        limitAdminsReached:
            'You have reached the maximum amount of administrators in your plan. Upgrade your subscription now and add more administrators.',
        limitUsersAlmostReached:
            'You have reached 80% of the maximum amount of members in your plan. Once you have reached the limit, no more members can join your community. To ensure that all members can sign up, we recommend you to upgrade your subscription.',
        limitUsersReached:
            'You have reached the maximum amount of members in your plan. Upgrade your subscription now and invite more members.',
        linkmustbevalidurl: 'Link must be valid URL',
        audioUnavailable:
            'This audio is unavailable. It may have been removed, made private, or the audio provider may be temporarily unavailable.',
        audioUrlMustBeSupported: 'Enter a valid Spotify, Apple Podcasts, SoundCloud, Podigee, or Mixcloud URL',
        audioUrlSupportedPlatforms: 'Supported platforms: Spotify, Apple Podcasts, SoundCloud, Podigee, and Mixcloud.',
        videoUrlMustBeSupported: 'Enter a valid YouTube, Vimeo, Loom, TikTok, or Kinescope video URL',
        videoUrlSupportedPlatforms: 'Supported platforms: YouTube, Vimeo, Loom, TikTok, and Kinescope.',
        videoUnavailable:
            'This video is unavailable. It may have been removed, made private, or the video provider may be temporarily unavailable.',
        login: {
            registratonIsNoPossible:
                'Unfortunately, no further registrations are possible at the moment. Please try again later or contact the administrators.',
            registratonIsNoPossibleTitle: 'No registrations possible',
        },
        managespacelocks: 'Manage space locks',
        manageuserkeys: 'Manage user keys',
        markasfeatureddesc: 'Flag new events as featured on import.',
        markasstickydesc: 'Pin events to the top of your lists by default.',
        marketiterestsubmit: 'We will inform the author about your interest.',
        matching: {
            noMatchesFound: 'No matching profiles found. Try updating the profile information to improve matches.',
        },
        maxcharacters: 'max. {count} characters',
        memberInvoicesTitle: "{name}'s upcoming and past invoices",
        memberJoinedCommunity: '{name} joined your Community',
        membersCsvDownloadTitle: 'Download the data shown in this table as CSV file',
        membersCsvExportFailed: 'The member CSV export failed. Please try again.',
        membersCsvExportProgressInfo:
            'The export loads all matching members in batches and downloads the CSV when the last batch is ready.',
        membersCsvExportProgressStatus: '{current} of {total} members prepared',
        membership: {
            subscriptionUpdated: 'Subscription was updated successfully.',
        },
        membershipCancelFailed: 'Error cancelling membership',
        membershipCancelled: 'Membership cancelled',
        membershipCancelScheduled: 'The membership of {name} will be cancelled on {date}',
        membershipStatus: 'Status: {status}',
        membershipStatusCancelled: 'Status: {status}. Cancelled to: {date}',
        membersLoadingLonger:
            'In larger communities it might take a while to load and process all members. Please be patient. Pages loaded: {pagesLoaded}',
        monetizationCollection: {
            subscriptions: {
                cancelationdateinfo:
                    'Users can always immediately upgrade their subscription to a plan with a higher price per billing cycle. The price per billing cycle is calculated including booked upgrades.',
            },
        },
        monetizationHelpPrefix: 'Read our',
        monetizationHelpSuffix: 'to learn more on how to accept payments.',
        monetizationPayments: 'Configure automated payments',
        monetizationPlans: 'Configure memberships and prices',
        monetizationTimepasses: 'Configure time passes for members',
        monetizationUpgrades: 'Configure extras that can be used in membership plans or bookings',
        moreinfoonourwebsite: 'For more options, please visit the community in your browser.',
        nameRequired: 'Name is required',
        noActiveSubscriptionDescription:
            'Your community has no active subscription. Please subscribe to continue using coapp.',
        noActiveTimePass: 'No active time pass',
        noActivityYet: 'No activity yet',
        noBillingAddress: 'User does not have a billing address yet',
        noChannelsYet: "You don't have any channel yet. Create some.",
        noConnectedCalendarAccounts: "You don't have any connected calendar accounts yet.",
        nodefaultplanselected: 'No default plan is selected',
        noEventsAttending: 'You are not attending an event yet.',
        noPlanAssigned: 'No plan assigned',
        noplanavailable: 'There are currently no plans available for you.',
        noplanselected: 'No plan is selected',
        noPlansYet: 'You have not created any plans yet.',
        noResourcesToBlock: 'There is no resource to block.',
        norowsloaded: 'No rows loaded yet. Upload a CSV or add a row manually.',
        noTaxRatesYet: 'You have not created any tax rates yet.',
        noUpcomingPlan: 'There is no upcoming plan',
        noUpgradesAvailable: 'no upgrades available',
        noUpgradesYet: 'You have not created any upgrades yet.',
        noZapierKeys: "You don't have any api keys yet. Create one.",
        noDefaultAvailabilitySet: 'No default availability set.',
        nukiAuthUrlError: 'Error while getting NUKI auth url.',
        nukiImportDescription: 'Import your Smart Locks 3.0 to use in your resources.',
        nukiVerifyFailed: 'Cannot verify if the NUKI account is already connected',
        page: {
            privatePageVisibilityInfo: 'This page is only visible to page members and admins.',
        },
        pagesLoadFailed: 'Error while loading pages. Please try again or send a bug report.',
        passwordlength: 'Password can not be longer than 56 characters',
        passwordResetRequired: 'Password reset required on next login',
        paymentActivatedBody: 'To set a price you need to create at least one public plan.',
        paymentActivatedTitle: 'Payment activated:',
        paymentBillingInactivePrefix: 'Payment and billing is not active. Please',
        paymentBillingInactiveSuffix: 'to use paid member plans and upgrades.',
        paymentMethodDetached: 'Payment method detached',
        paymentMethodDetachFailed: 'Could not detach payment method. Please contact support via Intercom widget.',
        paymentMethodsDescription: 'Manage allowed payment methods for your members',
        paymentMethodsStripeNotice: 'You must activate all payment methods in Stripe before turning them on here.',
        paymentNotActivatedBody: 'To give a resource a price you need to activate payment.',
        paymentNotActivatedTitle: 'Payment not activated:',
        passwordResetAdminInfo:
            'As an admin, you can reset the password for this user. The user will receive an email with instructions to set a new password.',
        passwordResetAdminLinkSent: 'Password reset link sent to user email.',
        passwordResetAdminLinkSendFailed: 'Failed to send password reset link. Please try again or contact support.',
        permissions: {
            updateFailed: 'Failed to update permissions',
            updateSuccess: 'Permissions updated successfully',
            approval: 'New signups require reviewing and accepting by admins, before they can join.',
            inviteonly: 'New users must be invited or created by admins. Users cannot sign up on their own.',
            open: 'Anyone can sign up without the help of an admin. If you have activated payments, users always have to choose one of the plans to be able to sign up.',
        },
        plancheckout: {
            planupdatefailed: 'Failed to update your plan. Please try again.',
            planupdatesuccess: 'Your plan has been updated successfully.',
        },
        planCreated: 'Plan created',
        planCreateFailed: 'Failed to create plan',
        planDuplicateCreated: 'Plan duplicate created',
        planDuplicateCreateFailed: 'Failed to create plan duplicate',
        planUpdateFailed: 'Failed to edit plan',
        planUpgradeCreated: 'Plan upgrade created',
        planUpgradeCreateFailed: 'Failed to create plan upgrade',
        planUpgradeFetchFailed: 'Failed to get plan upgrade',
        planUpgradeUpdated: 'Plan upgrade updated',
        planUpgradeUpdateFailed: 'Failed to update plan upgrade',
        popupBlocked: 'Please allow popups for this website.',
        postDraftRestored: 'Draft restored.',
        postDraftSaved: 'Draft saved.',
        postVisibility: {
            eventAudience: 'Only admins/organizers and participants can see this post.',
            feedAndEvent:
                'This post will appear in the feed and on this event. On the event page, only admins/organizers and participants can see it.',
            feedOnly: 'This post will appear in the feed and is visible to all members.',
            privatePageOnly:
                'This post will only appear on this private page and is visible to page owners, admins, and contributors.',
            privatePageOnlyWithName:
                'This post will only appear on the private page "{pageName}" and is visible to page owners, admins, and contributors.',
            publicPageFeedAndParent:
                'This post will appear in the feed and on the public page "{pageName}" and is visible to all members and visitors.',
            publicPageFeedAndParentLocked:
                'This post will appear in the feed and on this public page and is visible to all members and visitors.',
            publicPageOnly: 'This post will only appear on this public page and is visible to all visitors.',
        },
        pricePerSlotHelp:
            'If set, the user is charged this price for the whole slot booking instead of calculating the price based on the hours booked.',
        processedRows: 'Processed {current} of {total}',
        readHelpArticlePrefix: 'Read our',
        readHelpArticleSuffix: 'to learn how to use rules function.',
        redirecturlhelp: 'Copy this URL and paste in the settings of the provider',
        reduceFeesPrefix: 'To reduce your application fees',
        registrationAddressUpdated: 'Registration address updated',
        registrationAddressUpdateFailed: 'Could not update registration address. Please try again later.',
        registrationconfirmationmessage: 'You can find your ticket at any time on the event page.',
        removeCobrandingDoItLater: "I'll do it later",
        removeCobrandingMsg: 'Upgrade to a paid plan, now.',
        removeCobrandingTitle: 'Upgrade to remove coapp branding',
        resendconfirmationcode: 'Resend code',
        resendconfirmationcodein: 'Resend code in {countdown}',
        resourceCreated: 'Resource created',
        resourceCreateFailed: 'Could not create resource',
        resourceDescriptionHelp: 'Short description and/or important notes about this resource.',
        resourceLocationHelp: 'Where is this resource located?',
        resourceNameHelp: 'Name will be displayed to users on booking page.',
        resourceSyncFailed: 'Could not sync resource',
        resourceSyncStarted: 'Resource sync started',
        savetopreview: 'Save to preview',
        paywallSectionHelp:
            'Members only see the content above this divider. Everything below it stays behind the paywall.',
        scopeshelp: 'The scopes should be comma separated',
        searchnomatches: 'No results match your search criteria',
        searchNoResults: 'No results found for "{term}"',
        selectConfiguration: 'Select your configuration',
        selectedFile: 'Selected file: {file}',
        sepaContactSupport: 'To make changes to your SEPA-Payment configuration,',
        sepaDirectDebitInfo:
            'Would you rather like to pay by SEPA direct debit or bank transfer? Would you like to discuss Enterprise pricing or custom solutions? {0}.',
        sepaLimitsNotice:
            'By default, SEPA direct debit transactions have a limit of EUR 10,000 each. For new customers, there is an additional weekly total limit of EUR 10,000. For more information on SEPA payment limits, see',
        settingsAccessibility:
            'This text is published on the Accessibility Policy page, linked from the public footer and the More menu in the member navigation. Use it to describe the accessibility of your community and how people can report barriers or request assistance.',
        settingsAccount: 'Manage your coapp subscription, payment methods and invoices',
        settingsAuthentication: 'Integrate authentications from other services',
        settingsCustomization: 'Setup your logo, colors and intro text',
        settingsDomain: 'Customize domain name',
        settingsFeatures: 'Turn on and off your community features',
        // Descriptions for the legal and customer settings cards.
        settingsLegalAndCompliance: 'Manage legal documents and the purchase withdrawal form',
        settingsCustomerNumberSettings: 'Assign customer numbers to existing members',
        settingsGeneral: 'Here you can manage general settings',
        settingsImprint:
            'This text is published on the combined Imprint & Privacy Policy page, linked from the public footer, login page and member navigation. During signup, new members are asked to acknowledge the privacy policy.',
        settingsIntegrations: 'Connect external services to your coapp community',
        settingsJavaScript: 'Custom javascript injection',
        settingsPermissions: 'Manage permissions for your community',
        settingsTerms:
            'These terms are published on the Terms and Conditions page, linked from the public footer, login page and the More menu in the member navigation. Members are asked to accept them during signup and when making purchases of any products or services.',
        signup: {
            accountexists: 'Already have an account?',
            chooseyourplan: 'Select your plan to see the details and start the registration process.',
            jointhecommunity: 'Sign up',
            noplanavailable: 'There are currently no plans available for you.',
        },
        signupV2: {
            email: 'Enter your email',
            emailSubtitle: 'Start by entering the email address you want to use.',
            termsandprivacydisclaimer: 'By using this platform, you agree to our {0} & {1}.',
            otp: 'Enter the code',
            name: 'Enter your full name',
            nameRequired: 'Enter your first and last name to continue.',
            password: 'Create a password',
            passwordSubtitle: 'Choose a password with at least 15 characters.',
            passwordRequired: 'Use at least 15 characters for your password.',
            joinCommunity: 'Join community',
            avatarAlt: 'Avatar for {name}',
            successTitle: 'Your account has been created successfully',
            redirectManually: 'Click here if you’re not redirected automatically',
            waitingForApprovalTitle: 'Registration complete',
            waitingForApprovalMessage: 'Your account still needs to be approved.',
            waitingForApprovalEmail: 'You’ll receive an email as soon as you can get started.',
            fieldsRequired: 'Complete all required fields to continue.',
            review: 'Complete signup',
            reviewDescription: 'Review your details for {email} and accept the agreements to sign up.',
            pending: 'Your registration is awaiting approval.',
            denied: 'Registration is unavailable for this account.',
            codeSent: 'Check the email we sent to {email} and enter the code. Check your Spam too.',
            code: 'Verification code',
            verify: 'Verify code',
            resend: 'Send new code',
            resendIn: 'You can ask for a new code in {seconds} seconds.',
            select: 'Select an option',
            error: 'Signup could not be completed. Please try again.',
            restart: 'We couldn’t finish creating your account. Enter your email again to get a new verification code.',
            rateLimited: 'Too many attempts. Please wait {seconds} seconds and try again.',
            accept: 'I accept the',
            acceptRequired: 'Please accept the required agreements.',
            accountExists: 'An account already exists. Please log in.',
        },
        signupStrategyApproval:
            'Choose Approval based if you want to review registrations before accepting or rejecting them. You can still create or invite members. If you have enabled payments in your community, this strategy is not available.',
        signupStrategyApprovalNotice:
            'Your sign up strategy is set to approval. In order to connect stripe, please change your sign up strategy to another option.',
        signupStrategyChangeRequired: 'Please change your sign up strategy to connect stripe',
        signupStrategyInviteOnly:
            'Choose By invitation only if you only want users to join that are invited or added to your community by your admins. The public signup area will be disabled.',
        signupStrategyOpen:
            'Choose Anyone can sign up if you want everyone to be able to sign up without the help of an admin. If you have activated payments, users always have to choose one of the plans to be able to sign up. You can still create or invite members manually.',
        signupStrategyUpdated: 'Sign-up strategy updated successfully',
        signupStrategyUpdateFailed: 'Failed to update sign-up strategy',
        spaceSettingsLoadFailed: 'Failed to get space settings',
        spaceSettingsRefreshFailed: 'Could not get updated space settings.',
        spaceSettingsSaveFailed: 'Failed to save space settings',
        startsAt: 'Starts at: {date}',
        stripeConnectedTitle: 'Stripe is connected and working',
        stripeConnectFailed: 'Failed to connect Stripe. Please contact our support team.',
        stripeDetachSupport: 'If you want to deattach your stripe account from coapp, please contact our support team.',
        stripeFeesInfo:
            'Stripe charges an additional fee for "Stripe Billing" plus payment fees varying depending on the payment method chosen. An overview of Stripe payment fees can be found',
        stripeNeedsInfoDescription: 'Your Stripe account misses some information, before it can be activated.',
        stripeNeedsInfoTitle: 'Stripe needs more information',
        stripePaymentsIntro: 'coapp uses Stripe to provide payments and billing services for your community.',
        stripePendingDescription:
            "Pending means the account is being reviewed or verified by Stripe. This occurs when Stripe is verifying the information that was provided, such as an ID document upload. This might take a moment. If this status doesn't change please",
        stripePendingTitle: 'Stripe connection is pending',
        stripeSetupPrompt:
            'In order to set up coapp Payments by Stripe you have to create a Stripe Account first. Ready to get started?',
        stripeStartAcceptingTitle: 'Start accepting payments now',
        strongTagsHint:
            'Only the first 10 tags will be shown in the user list, so choose the most important ones first.',
        subscriptionCancelledNotice: 'Your coapp subscription was cancelled. It will terminate on',
        subscriptionAccessRestrictedDescription:
            'The billing manager can manage the community subscription, change the plan, open invoices, or update payment methods.',
        deactivateOrDeleteCommunitySupportMessage:
            'Hi coapp Support Team, I would like to deactivate or delete my community.',
        subscriptionManagedByOwner: 'Only the billing manager can manage the coapp subscription.',
        subscriptionManageDescription:
            'Manage your subscription, download invoices and configure your payment credentials.',
        suspension: {
            community:
                'This community is currently suspended. Please contact customer service to clarify the situation.',
            communityNotAvailable: 'Community not available',
            permanentBan:
                'This community has been permanently banned due to violations of our terms of service. If you believe this is a mistake, please contact customer support for further assistance.',
        },
        taxRateActive: 'Tax rate is active',
        taxRateActiveHelp:
            'By default set to true. When unset this tax rate cannot be used with new checkout sessions, but will still work for subscriptions and invoices that already have it set.',
        taxRateCreated: 'Tax rate created',
        taxRateCreateFailed: 'Failed to create tax rate',
        taxRateDescription: 'Define tax rates for plans, upgrades and resources',
        taxRateDescriptionHelp:
            'Details about the tax rate for your internal use only. It will not be visible to your customers.',
        taxRateDisplayNameHelp: "The display name of the tax rate, which will be shown to users. E.g. 'VAT'",
        taxRateLoadFailed: 'Failed to load tax rate',
        taxRatePercentHelp: 'This represents the tax rate percent out of 100.',
        taxRateRequired: 'Please create at least a one tax rate',
        taxRateRequiredForPlanUpgrade: 'Please create at least a one tax rate before creating a plan upgrade',
        taxRatesPrefilled: 'Tax rates pre-filled automatically. You can change it if you want.',
        taxRateStripeSetupNotice: 'Before creating tax rates, you have to setup your Stripe Connection first.',
        taxRateTypeHelp: 'This specifies if the tax rate is inclusive or exclusive.',
        taxRateUpdated: 'Tax rate updated',
        taxRateUpdateFailed: 'Failed to update tax rate',
        termsAndConditionsHelp:
            'If this field is set, user will be required to accept terms and conditions before booking.',
        timepass: {
            applyFailed: 'Failed to apply Time Pass',
            applyForUserFailed: 'Failed to apply Time Pass for user',
            applyForUserSuccess: 'Successfully applied Time Pass for user',
            applySuccess: 'Successfully applied Time Pass',
            userHasActivePass: 'User already has an active Time Pass',
            youHaveActivePass: 'You already have an active Time Pass',
        },
        timepassAlreadyActive: 'You already have an active Time Pass',
        timepassApplied: 'Successfully applied Time Pass',
        timepassApplyFailed: 'Failed to apply Time Pass',
        timepassCreateFailed: 'Failed to create time pass',
        timepassDeleted: 'Successfully deleted time pass',
        timepassUpdateFailed: 'Failed to update time pass',
        timepassUsageDescription: 'Number of times a user can apply this time pass',
        timepassWalletLoadFailed: "Can't load time passes in user's wallet",
        tokenurlhelp: 'Endpoint to get the access token',
        toUseThisFeatureActivatePayments: 'To give a ticket a price you need to activate payments.',
        unsavedchangeswarning: 'You have unsaved changes. Are you sure you want to leave?',
        unsavedwarning: 'Your changes will be lost. Are you sure?',
        upgradeBarText: 'More features, bigger community – <strong>Upgrade now</strong>',
        upgradeBarTextMobileApp: 'A free community created with <strong>coapp</strong>',
        upgradesSetupPaymentsNotice:
            'Before creating paid plans and upgrades, you have to setup automated payments using Stripe and create at least one Tax rate.',
        uploadImageFailed: 'Could not upload image.',
        uploadingimages: 'Uploading images',
        useapikey: 'Use this key to authorize access to you space by Zapier',
        userApprovalFailed: 'User approval failed',
        userApproved: 'User approved',
        userDataLoadFailed: "Can't get user data",
        userDeleted: 'User deleted',
        userDeleteFailed: 'Could not delete user',
        userHasNoPaymentMethods: '{name} has no payment methods',
        userinfourlhelp: 'Endpoint to get user info',
        userMigrated: 'User was migrated to coapp',
        usernotexists: 'User does not exist or was deleted',
        userRejected: 'User has been rejected',
        userRejectionFailed: 'User rejection failed',
        userstatus: {
            3: 'Your account is currently suspended. This is probably due to a payment problem. Please contact customer service to clarify the situation.',
            4: 'Your account is currently canceled. Please subscribe to a plan to reactivate your membership.',
            5: 'Your account is currently waiting to be approved by an admin. As soon as your account has been approved, you will receive an email. Please make sure that you have confirmed your e-mail address. You have already received a separate email for this purpose.',
            7: "Your account is in guest status. As a guest, you can access 'My Events' and view public events. The rest of the community is unavailable. Contact the team to change your status.",
            7.2: "Your account is in guest status. As a guest, you can access 'My Events' and view public events. The rest of the community is unavailable. To upgrade, book a membership plan in the 'Membership' section.",
        },
        visibilitysyncnotice: 'Only events with these visibilities will sync.',
        zapierInviteNotice:
            'coapp.io integration with Zapier is not public yet. Click on invite link below to start using integration.',
    },
    meta_title: {
        createYourCommunity: 'Create your community',
    },
    notFound: {
        actions: {
            back: 'Go Back',
            primary: {
                event: 'Go to Events',
                generic: 'Back to Home',
                marketItem: 'Go to Marketplace',
                page: 'Go to Pages',
                post: 'Back to Home',
                user: 'Go to Community',
            },
        },
        code: 'ERROR 404',
        messages: {
            event: 'The event you’re looking for was deleted, never existed, the link changed, or you lack permission.',
            generic:
                'The content you’re looking for was deleted, never existed, the link changed, or you lack permission.',
            marketItem:
                'The marketplace item you’re looking for was deleted, never existed, the link changed, or you lack permission.',
            page: 'The page you’re looking for was deleted, never existed, the link changed, or you lack permission.',
            post: 'The post you’re looking for was deleted, never existed, the link changed, or you lack permission.',
            user: 'The member you’re looking for was deleted, never existed, the link changed, or you lack permission.',
        },
        titles: {
            event: 'EVENT NOT FOUND',
            generic: 'CONTENT NOT FOUND',
            marketItem: 'MARKET ITEM NOT FOUND',
            page: 'PAGE NOT FOUND',
            post: 'POST NOT FOUND',
            user: 'MEMBER NOT FOUND',
        },
    },
    page: {
        usecaselabel: 'How would you like to use this page?',
        private: 'Private page',
        privateDescription: `The page is private and only visible to members of the page. It won't be listed in the channel and won't be discoverable for non-members. Only owners can manually add members to the page. This is the best option if you want to create a page for a specific project ortopic and only want a limited number of people to have access to it.`,
        community: 'Community page',
        communityDescription:
            'The page is visible to all members of the community and listed in the channel. This is the best option if you want to create a page for a specific project or topic and want everyone in the community to have access to it.',
        paid: 'Page with exclusive content',
        paidDescription:
            'Pages with exclusive content are visible and discoverable by all members. Choose the section where exclusive access begins. Everything above remains visible to everyone, while everything below is only accessible to members with the required membership plan. Members can unlock access by selecting the appropriate plan. Ideal for courses, member areas, and exclusive content.',
        paidmembership: 'Required membership plan',
        exclusive: 'Exclusive',
    },
    paywall: {
        cta: 'Upgrade now',
        cta2: 'Request a quote',
        featureCopy: {
            // Permission management starts with the Professional plan.
            permissions: {
                free: {
                    bullets: [
                        '500 members',
                        '3 admins',
                        'API, Zapier & OAuth2',
                        'Only 5% platform fees',
                        '200 GB of storage space',
                    ],
                    headline: 'Manage who can create content',
                    subline: 'Switch to the Professional plan now for €99 a month to manage permissions.',
                },
                starter: {
                    bullets: [
                        '500 members',
                        '3 admins',
                        'API, Zapier & OAuth2',
                        'Only 5% platform fees',
                        '200 GB of storage space',
                    ],
                    headline: 'Manage who can create content',
                    subline: 'Switch to the Professional plan now for €99 a month to manage permissions.',
                },
                unknown: {
                    bullets: [
                        '500 members',
                        '3 admins',
                        'API, Zapier & OAuth2',
                        'Only 5% platform fees',
                        '200 GB of storage space',
                    ],
                    headline: 'Manage who can create content',
                    subline: 'Switch to the Professional plan now for €99 a month to manage permissions.',
                },
            },
            'announce-post': {
                free: {
                    bullets: [
                        'Custom domain',
                        'Remove coapp branding',
                        'Unlimited profile fields',
                        '100 members',
                        '100 GB of storage space',
                    ],
                    headline: 'Notify all members about important updates',
                    subline: 'Switch to the Starter plan now for just €19 a month to promote posts.',
                },
                unknown: {
                    bullets: [
                        'Custom domain',
                        'Remove coapp branding',
                        'Unlimited profile fields',
                        '100 members',
                        '100 GB of storage space',
                    ],
                    headline: 'Notify all members about important updates',
                    subline: 'Switch to the Starter plan now for just €19 a month to promote posts.',
                },
            },
            'bigger-files': {
                free: {
                    bullets: [
                        'Custom domain',
                        'Remove coapp branding',
                        'Unlimited profile fields',
                        '100 members',
                        '100 GB of storage space',
                    ],
                    headline: 'Enable larger file uploads',
                    subline: 'Switch to the Starter plan now for just €19 a month to upload files up to 25MB in size.',
                },
                starter: {
                    bullets: [
                        '500 members',
                        '3 admins',
                        'API, Zapier & OAuth2',
                        'Only 5% platform fees',
                        '200 GB of storage space',
                    ],
                    headline: 'Enable larger file uploads',
                    subline: 'Switch to the Professional plan now for €99 a month to upload files up to 100MB in size.',
                },
                professional: {
                    bullets: [
                        '1,000 members',
                        '5 admins',
                        'Only 2% platform fees',
                        'Priority Support',
                        'White-label app (€399)',
                    ],
                    headline: 'Enable larger file uploads',
                    subline: 'Switch to the Business plan now for €399 a month to upload files up to 500MB in size.',
                },
                unknown: {
                    bullets: [
                        'Single file upload limits depend on your plan',
                        'Starter: 25 MB, Professional: 100 MB, Business: 500 MB',
                        'Share high-resolution media',
                        'Upload larger documents with ease',
                    ],
                    headline: 'Enable larger file uploads',
                    subline: 'Upgrade to increase file upload limits.',
                },
            },
            'custom-fields': {
                free: {
                    bullets: [
                        'Custom domain',
                        'Remove coapp branding',
                        'Unlimited profile fields',
                        '100 members',
                        '100 GB of storage space',
                    ],
                    headline: 'More custom fields for your profiles',
                    subline: 'Switch to the Starter plan now for just €19 a month to create unlimited custom fields.',
                },
                unknown: {
                    bullets: [
                        'Advanced member data structures',
                        'High-volume field management',
                        'Activation via our support team',
                    ],
                    headline: 'More custom fields for your profiles',
                    subline: 'Please contact our support team to customize your profile settings.',
                },
            },
            customization: {
                free: {
                    bullets: [
                        'Custom domain',
                        'Remove coapp branding',
                        'Unlimited profile fields',
                        '100 members',
                        '100 GB of storage space',
                    ],
                    headline: 'Design your platform',
                    subline:
                        'Switch to the Starter plan now for just €19 a month and customise your platform to your liking.',
                },
                unknown: {
                    bullets: [
                        'Customize your logo, favicon and colors',
                        'Available on Starter, Professional and Business',
                        'Create a branded community experience',
                    ],
                    headline: 'Design your platform',
                    subline: 'Upgrade your plan and customise your platform to your liking.',
                },
            },
            'branded-domain': {
                free: {
                    bullets: [
                        'Custom domain',
                        'Remove coapp branding',
                        'Unlimited profile fields',
                        '100 members',
                        '100 GB of storage space',
                    ],
                    headline: 'Use your own domain',
                    subline: 'Switch to the Starter plan now for just €19 a month and connect your own domain.',
                },
                unknown: {
                    bullets: ['Custom domain', 'Support-assisted setup', 'Professional appearance'],
                    headline: 'Use your own domain',
                    subline: 'Upgrade your plan to connect your own domain.',
                },
            },
            'more-admins': {
                free: {
                    bullets: [
                        '500 members',
                        '3 admins',
                        'API, Zapier & OAuth2',
                        'Only 5% platform fees',
                        '200 GB of storage space',
                    ],
                    headline: 'Add more administrators',
                    subline: 'Switch to the Professional plan now for €99 a month and add up to 3 admins.',
                },
                starter: {
                    bullets: [
                        '500 members',
                        '3 admins',
                        'API, Zapier & OAuth2',
                        'Only 5% platform fees',
                        '200 GB of storage space',
                    ],
                    headline: 'Add more administrators',
                    subline: 'Switch to the Professional plan now for €99 a month and add up to 3 admins.',
                },
                professional: {
                    bullets: [
                        '1,000 members',
                        '5 admins',
                        'Only 2% platform fees',
                        'Priority Support',
                        'White-label app (€399)',
                    ],
                    headline: 'Add more administrators',
                    subline: 'Switch to the Business plan now for €399 a month and add up to 5 admins.',
                },
                business: {
                    bullets: ['Individual offer', 'Preferred support', '1000+ members'],
                    headline: 'Add more administrators',
                    subline:
                        'Reached the admin limit? We’d be happy to put together a personalised Enterprise quote with the number of admins you require.',
                },
                unknown: {
                    bullets: ['More admin seats', 'Share management tasks', 'Remove limits'],
                    headline: 'Add more administrators',
                    subline: 'Switch the plan now and add up to 5 admins.',
                },
            },
            'more-channels': {
                free: {
                    bullets: [
                        'Custom domain',
                        'Remove coapp branding',
                        'Unlimited profile fields',
                        '100 members',
                        '100 GB of storage space',
                    ],
                    headline: 'Create more channels',
                    subline: 'Switch to the Starter plan now for just €19 a month to create up to 5 channels.',
                },
                starter: {
                    bullets: [
                        '500 members',
                        '3 admins',
                        'API, Zapier & OAuth2',
                        'Only 5% platform fees',
                        '200 GB of storage space',
                    ],
                    headline: 'Create more channels',
                    subline:
                        'Switch to the Professional plan now for €99 a month to create an unlimited number of channels.',
                },
                unknown: {
                    bullets: [
                        'Organise knowledge and discussions',
                        'Improve the clarity of your community',
                        'Set permissions for each channel',
                    ],
                    headline: 'Create more channels',
                    subline:
                        'You have reached the channel limit. Please contact our support team to increase your capacity.',
                },
            },
            'more-members': {
                free: {
                    bullets: [
                        'Custom domain',
                        'Remove coapp branding',
                        'Unlimited profile fields',
                        '100 members',
                        '100 GB of storage space',
                    ],
                    headline: 'Grow your community',
                    subline: 'Switch to the Starter plan now for just €19 a month, so your community can keep growing.',
                },
                starter: {
                    bullets: [
                        '500 members',
                        '3 admins',
                        'API, Zapier & OAuth2',
                        'Only 5% platform fees',
                        '200 GB of storage space',
                    ],
                    headline: 'Grow your community',
                    subline: 'Switch to the Professional plan now for €99 a month, so your community can keep growing.',
                },
                professional: {
                    bullets: [
                        '1,000 members',
                        '5 admins',
                        'Only 2% platform fees',
                        'Priority Support',
                        'White-label app (€399)',
                    ],
                    headline: 'Grow your community',
                    subline: 'Switch to the Business plan now for €399 a month, so your community can keep growing.',
                },
                business: {
                    bullets: ['Individual offer', 'Preferred support', '1000+ members'],
                    headline: 'Grow your community',
                    subline:
                        'Reached the members limit? We’d be happy to put together a personalised Enterprise quote with the number of members you require.',
                },
                unknown: {
                    bullets: ['Higher member cap', 'Scale your community', 'Remove limits'],
                    headline: 'Grow your community',
                    subline: 'Upgrade to increase your member limit.',
                },
            },
            'more-storage': {
                free: {
                    bullets: [
                        '100 members',
                        'Custom domain',
                        'Remove coapp branding',
                        'Unlimited profile fields',
                        '100 GB of storage space',
                    ],
                    headline: 'Increase your storage capacity',
                    subline:
                        'Switch to the Starter plan now for just €19 a month and get 100GB of storage space for your files.',
                },
                starter: {
                    bullets: [
                        '500 members',
                        '3 admins',
                        'API, Zapier & OAuth2',
                        'Only 5% platform fees',
                        '200 GB of storage space',
                    ],
                    headline: 'Increase your storage capacity',
                    subline:
                        'Switch to the Professional plan now for €99 a month and get 200GB of storage space for your files.',
                },
                professional: {
                    bullets: [
                        '1,000 members',
                        '5 admins',
                        'Only 2% platform fees',
                        'Priority Support',
                        'White-label app (€399)',
                    ],
                    headline: 'Increase your storage capacity',
                    subline:
                        'Switch to the Business plan now for €399 a month and get 500GB of storage space for your files.',
                },
                business: {
                    bullets: ['Individual offer', 'Preferred support', '1000+ members'],
                    headline: 'Increase your storage capacity',
                    subline:
                        'Reached your storage capacity limit? We’d be happy to put together a bespoke Enterprise package with the storage capacity you need for your files.',
                },
                unknown: {
                    bullets: [
                        'Enable file sharing',
                        'More interaction thanks to increased storage capacity',
                        'All files in one central location',
                    ],
                    headline: 'Increase your storage capacity',
                    subline: 'Upgrade to increase your storage capacity.',
                },
            },
            'pin-post': {
                free: {
                    bullets: [
                        'Custom domain',
                        'Remove coapp branding',
                        'Unlimited profile fields',
                        '100 members',
                        '100 GB of storage space',
                    ],
                    headline: 'Pin posts to the top',
                    subline: 'Switch to the Starter plan now for just €19 a month to be able to pin posts.',
                },
                unknown: {
                    bullets: [
                        'Custom domain',
                        'Remove coapp branding',
                        'Unlimited profile fields',
                        '100 members',
                        '100 GB of storage space',
                    ],
                    headline: 'Pin posts to the top',
                    subline: 'Switch to the Starter plan now for just €19 a month to be able to pin posts.',
                },
            },
            'white-label': {
                free: {
                    bullets: [
                        'Custom domain',
                        'Remove coapp branding',
                        'Unlimited profile fields',
                        '100 members',
                        '100 GB of storage space',
                    ],
                    headline: 'Remove the coapp branding',
                    subline:
                        'Switch to the Starter plan now for just €19 a month to permanently remove the coapp branding.',
                },
                unknown: {
                    bullets: [
                        'Available on Starter, Professional and Business',
                        'Remove coapp branding',
                        'Deliver a fully branded community',
                    ],
                    headline: 'Remove the coapp branding',
                    subline: 'Upgrade to permanently remove the coapp branding.',
                },
            },
        },
    },
    placeholders: {
        actionbuttontitle: 'Enter title for action button',
        actionbuttonurl: 'Enter url/link for action button',
        additionalinfo: 'e.g. department',
        address: 'Street and number',
        addressadditionalinformation: "e.g. 'Appartment No. 1'",
        authtokenhelper: 'If your site requires a token',
        autocompleteempty: 'No results',
        categoryname: 'Enter category name',
        cf: {
            dataType: 'Select data type',
            editability: 'Select who can edit',
            identifier: 'Enter identifier',
            // custom fields related placeholders
            label: 'Enter label',
            optionsValues: 'e.g. option 1,option 2,option 3',
            requestedOnSignUp: 'Select sign up option',
            sortOrder: 'Enter sort order',
            visibility: 'Select who can see',
        },
        channelnameexample: 'Name (Project, StartUp, MeetUp)',
        comment: 'Leave a comment',
        communityname: 'Name your community',
        companyname: 'Company name',
        couponcode: 'Enter coupon code',
        date: 'Choose a date',
        domainexample: 'e.g. best-community',
        editorCode: '<html>Add your code here ...</html>',
        email: 'mail@example.com',
        emailfooter: 'Define your Footer/Disclaimer content here',
        invoiceNumber: 'e.g. INV-12345',
        enterauthurl: 'Enter the Auth URL',
        enterclientid: 'Enter the Client ID',
        enterclientsecret: 'Enter the Client Secret',
        entername: 'Enter a name',
        entertext: 'Enter text here ...',
        entertokenurl: 'Enter the Token URL',
        enteruserinfourl: 'Enter the User Info URL',
        file: 'No file selected',
        filechooseordrop: 'Choose a file or drop it here...',
        filedragdropupload: 'Drop file or click',
        filedrop: 'Drop file here...',
        filteritems: 'Filter items',
        groupname: 'Name your group',
        hexcode: 'hex code',
        image: 'Click to change the image',
        imagedragdropupload: 'Drop image or click',
        javascriptcode: 'JavaScript code',
        loginctatitle: 'Enter a title for the login button',
        marketitemcontact: 'link to a contact page or email',
        marketitemdescription:
            'The description of your item will be displayed on the detail page of your item and should describe it in detail.',
        marketitemsummary: 'The summary of your item will be displayed in the feed and in the market list.',
        marketitemtitle: 'Choose a good title :)',
        messagecreate: 'Write a message ...',
        nameresourcegroup: 'Name resource group',
        newPrice: 'New price',
        note: 'Add a Note',
        number: 'Number',
        optional: 'optional',
        password: 'enter your password',
        phonenumber: 'Phone number',
        placeholderoptional: 'Placeholder (optional)',
        planmultiselect: 'Select plans',
        post: '@:labels.create @.lower:labels.post',
        postalcode: '12345',
        posteditor: 'Write here ...',
        registrationlink: 'Public registration link',
        resourcename: 'Enter resource name',
        scopeexample: 'Example: scope1,scope2,scope3',
        search: 'search',
        // Search for Name or Email
        searchnameoremail: 'Search for name or email',
        searchaccesskey: 'Search access key to assign',
        searchaccesspolicy: 'Search access policy to assign',
        searchevent: 'Search an event',
        searchmember: 'Search for a member',
        searchmembers: 'Search for members',
        select: '@:placeholders.selectplease',
        selectcalendar: 'Select calendar',
        selectcollectionmethod: 'Select collection method',
        selectconnectedaccount: 'Select connected account',
        selectcountry: '-- Please select a country --',
        selectcsvfile: 'Select CSV file',
        selectlocation: 'Select location',
        selectlock: 'Select lock',
        selectplease: 'Please select an option',
        selectresource: 'Select resource',
        selectsalutation: 'Select salutation',
        selecttaxrate: 'Select tax rate',
        selectTimezone: 'Select a timezone',
        selectvisibilities: 'Select visibilities',
        signupctatitle: 'Enter a title for sign up button',
        spacename: 'Enter your space name',
        strongtagssuggestion: 'Strong tags suggestion',
        subtitle: 'Subtitle',
        subtitleoptional: 'Subtitle (optional)',
        supporturl: 'Support page url',
        tags: 'Start typing',
        taxratename: 'Enter tax rate name',
        termsandconditions: ' ',
        termsandconditionslink: 'Enter link to terms and conditions for this resource',
        textoptional: 'Text (Optional)',
        ticketname: 'e.g. Early Bird',
        ticketurlplaceholder: 'External ticket link',
        title: 'Enter title',
        typeanything: 'type anything',
        typehere: 'Type here',
        updatecreate: '@:labels.create @.lower:labels.update',
        url: 'https://',
        urlexample: 'https://example.com',
        audioUrl: 'https://open.spotify.com/episode/...',
        videoUrl: 'https://www.youtube.com/watch?v=...',
        vatid: 'GB123456789',
    },
    polls: {
        addoption: 'Add option',
        addpoll: 'Add poll',
        multiselect: 'Allow multiple answers',
        optionPlaceholder: 'Enter option',
        options: 'Poll options',
        poll: 'Poll',
        polls: 'Polls',
        pollsettings: 'Poll settings',
        pollvoteadded: 'Your vote has been recorded',
        pollvoteadderror: 'An error occurred while recording your vote. Please try again.',
        pollvoteremoved: 'Your vote has been removed',
        pollvoteremoveerror: 'An error occurred while removing your vote. Please try again.',
        question: 'Question',
        questionPlaceholder: 'Ask your community a question',
        voters: 'Voters',
        votes: 'Votes',
    },
    social: {
        facebook: 'Facebook',
        instagram: 'Instagram',
        linkedin: 'LinkedIn',
        reddit: 'Reddit',
        twitter: 'Twitter/X',
        youtube: 'YouTube',
    },
    units: {
        days: 'Day | Days',
        followers: 'Follower | Followers',
        hours: 'Hour | Hours',
        months: 'Month | Months',
        weeks: 'Week | Weeks',
        years: 'Year | Years',
    },
    userstatuslabels: {
        0: 'Active',
        1: 'Pending',
        2: 'Deleted',
        3: 'Suspended',
        4: 'Canceled',
        5: 'Waiting for approval',
        6: 'Rejected',
        7: 'Guest',
        8: 'Banned',
    },
};
export default en;
